// Mexico City, "Día de Muertos at Bellas Artes": the night of 1–2 November from a café terrace high on the south side of
// Avenida Juárez, facing east-north-east over the Alameda Central. The Palacio de Bellas Artes stands at the end of the
// park, white marble, its orange-and-gold tiled domes floodlit and the bronze Pegasus groups on its plaza; the Torre
// Latinoamericana rises right of it, behind the Art Deco La Nacional, where Juárez becomes Madero; the Centro Histórico's
// domes and bell towers lie beyond, the lit slopes of the Sierra de Guadalupe on the left and Iztaccíhuatl and
// Popocatépetl faint on the right horizon. Below: Juárez traffic (pink-and-white taxis, vochos, an ambulance), trolleybuses
// crossing on Eje Central, catrinas under marigold arches, vendors and an organillero. On the terrace: papel picado, an
// ofrenda with candles, sugar skulls, a xolo asleep on a sarape and a cup of hot chocolate. Cempasúchil petals drift down.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, px, rect, makeHalo, drawHalo, FONT3, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Scene constants ================= */
const ACCENT = '#8338ec';                            // Día de Muertos purple (the export block writes it out)
const HZ = 160;                                      // the valley's horizon: far rooftops meet the haze
const BA = { cx: 212, x0: 142, x1: 280, base: 174, tip: 86 };    // Palacio de Bellas Artes (main façade on the avenue)
const TL = { cx: 329, hw: 11, top: 50, tip: 24 };    // Torre Latinoamericana: shaft x 318..340, antenna tip row 24
const LN = { cx: 315, hw: 17, top: 120 };            // La Nacional, the 1932 Art Deco tower at the Eje Central corner
const CH = { cx: 58, g: 158 };                       // a tezontle church beyond the Alameda (bell tower + tiled dome)
const INS = { cx: 26, top: 128 };                    // Torre Insignia at Tlatelolco, far off on the left
const FL = { y: 180, x0: 276 };                      // Eje Central's far lane (trolleybuses), road row
const WALK = { y0: 180, y1: 188, x1: 278 };          // the Alameda's sidewalk and Bellas Artes' plaza front
const ST = { y0: 188, y1: 228 };                       // Avenida Juárez below
const LANES = [{ y: 198, dir: -1 }, { y: 208, dir: 1 }, { y: 218, dir: 1 }];   // wheel rows: westbound, eastbound, near
const NB = { x0: 346, roof: 186 };                   // the lower building next door (our side of the avenue)
const LEDGE = 228;                                   // top of the terrace parapet
const OF = { cx: 430, x0: 384, x1: 476 };            // the ofrenda at the right of the terrace
const BB = { x: 352, y: 124, w: 50, h: 22 };         // rooftop billboard face (exported as `billboard`)
const MOON = { x: 446, y: 50 };
const POPO = { x: 468, y: 139 };                     // Popocatépetl's crater

/* ================= Colour + pixel helpers ================= */
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const mixHex = (a, b, t) => { const A = hex(a), B = hex(b); return toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); };
const RGB = {};
const rgbOf = h => RGB[h] || (RGB[h] = hex(h));
/** ordered-dither pick from a palette (v in 0..1); soft < 1 narrows the dithered band between tones */
function dpick(pal, v, i, y, soft = 1) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, y) - 0.5) * soft) k++;
  return pal[Math.min(k, pal.length - 1)];
}
/** ImageData-backed painter for prerendering (hex colours). `own` tracks which building owns a pixel. */
function painter(w = W, h = H, track = false) {
  const [c, x] = makeCanvas(w, h);
  const img = x.createImageData(w, h), d = img.data;
  const own = track ? new Int32Array(w * h).fill(-1) : null;
  const p = { c, x, d, w, h, own, id: -1 };
  p.set = (i, y, col) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const k = rgbOf(col), o = (y * w + i) * 4;
    d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = 255;
    if (own) own[y * w + i] = p.id;
  };
  p.has = (i, y) => i >= 0 && y >= 0 && i < w && y < h && d[(y * w + i) * 4 + 3] > 0;
  p.done = () => { x.putImageData(img, 0, 0); return c; };
  return p;
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length));
  const p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function paintRows(p, rows, key, x0, y0, flip = false) {
  const w = Math.max(...rows.map(r => r.length));
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(x0 + (flip ? w - 1 - i : i), y0 + j, col); } });
}
function flipCanvas(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}
/** set pixel (i, y) and its mirror about column c; `col` may be a function of x (colour-only shading) */
function mset(p, c, i, y, col) {
  const j = 2 * c - i;
  p.set(i, y, typeof col === 'function' ? col(i) : col);
  if (j !== i) p.set(j, y, typeof col === 'function' ? col(j) : col);
}
/** symmetric run of columns c-hw..c+hw on row y */
function run(p, c, y, hw, col) { for (let i = c - hw; i <= c; i++) mset(p, c, i, y, col); }
/** FONT3 text (plus a few local glyphs) straight into a painter, at scale k, colour picked per glyph row */
const XGLYPH = { 'É': ['001', '111', '110', '100', '111'], 'Á': ['001', '010', '101', '111', '101'], $: ['111', '110', '111', '011', '111'] };
function ptext(p, s, x, y, colAt, k = 1) {
  let cx = x;
  for (const ch of s) {
    const g = XGLYPH[ch] || FONT3[ch];
    if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1')
      for (let a = 0; a < k; a++) for (let b = 0; b < k; b++) p.set(cx + i * k + a, y + j * k + b, colAt(j, i)); });
    cx += 4 * k;
  }
}

/* ================= Sky: indigo night, warming to a mauve city glow and a thin haze over the valley ================= */
const SKY = ['#050416', '#08061d', '#0c0925', '#110c2e', '#170f37', '#1e1340', '#261748', '#2f1b50', '#3a2057',
  '#46255d', '#532b62', '#613166', '#6f3868', '#7d4069', '#8a4968', '#955366'].map(hex);
function renderSky() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 172), 1.55);
    v += Math.max(0, 1 - Math.hypot((i - 250) / 280, (y - 170) / 90)) * 0.12;     // the Centro's glow
    v += Math.max(0, 1 - Math.hypot((i - 80) / 140, (y - 160) / 50)) * 0.05;      // the northern suburbs
    v -= Math.max(0, 1 - Math.hypot((i - MOON.x) / 70, (y - MOON.y) / 60)) * 0.04; // moon-washed air reads bluer
    const f = clamp(v, 0, 1) * (SKY.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = SKY[Math.min(k, SKY.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
const STARS = [];
{
  const r = rng(12);
  while (STARS.length < 34) {                        // a dry-season sky, but the city washes out most stars
    const x = Math.floor(r() * W), y = 22 + Math.floor(r() * 80);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 22 || (x > 312 && x < 348)) continue;
    STARS.push({ x, y, k: 17 + Math.floor(r() * 70), ph: r(), b: 0.25 + r() * 0.5, big: r() < 0.08 });
  }
}
/** waning gibbous, lit from the left, a few maria */
function renderMoon() {
  const R = 7, s = 2 * R + 3, c0 = R + 1, [c, x] = makeCanvas(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - c0, dy = y - c0, d1 = Math.hypot(dx, dy);
    if (d1 > R + 0.3) continue;
    const edge = R * 0.5 * Math.sqrt(Math.max(0, 1 - (dy / R) ** 2));             // the terminator, bulging right
    if (dx > edge + 0.6) continue;                                                  // the dark limb: sky shows through
    let col = dx > edge - 0.9 ? '#cdc6b2' : d1 > R - 0.9 ? '#e8e4d0' : '#fbf6e2';
    if ((dx + 2) ** 2 + (dy + 1) ** 2 < 5 || (dx - 1) ** 2 + (dy - 3) ** 2 < 3 || (dx + 4) ** 2 + (dy - 2) ** 2 < 2) col = '#d6d0bc';
    px(x, i, y, col);
  }
  return c;
}
/* thin dry-season wisps (tileable), lit mauve from the city below */
function makeWisps(seed, y0, h, k, top, body) {
  const r = rng(seed), puffs = [];
  for (let b = 0; b < 4; b++) {
    const bx = r() * W, bw = 50 + r() * 90, by = h * (0.3 + r() * 0.5);
    for (let i = 0; i < 6; i++) puffs.push({ x: bx + (r() - 0.5) * bw, y: by + (r() - 0.5) * 3, rx: 12 + r() * 22, ry: 1.2 + r() * 1.6 });
  }
  const p = painter(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) { let dx = x - q.x; dx -= Math.round(dx / W) * W; best = Math.max(best, 1 - (dx / q.rx) ** 2 - ((y - q.y) / q.ry) ** 2); }
    best += 0.25 * Math.sin(TAU * 9 * x / W + y * 1.3 + seed);
    if (best + (bayer(x, y) - 0.5) * 0.5 <= 0.15) continue;
    p.set(x, y, best > 0.6 ? body : top);
  }
  return { c: p.done(), y: y0, k };
}

/* ================= The volcanoes: Iztaccíhuatl and Popocatépetl, 60-70 km off, faint under the moon ================= */
// Iztaccíhuatl, "the sleeping woman": a long low ridge (head, chest, knees) on the left; Popocatépetl's broad cone on
// the right, farther off, its crater flat-topped. Both barely darker than the hazy sky, snow pale in the moonlight.
const iztaY = x => 158 - 11 * smooth(388, 402, x) * (1 - smooth(446, 462, x))
  - 3 * Math.exp(-(((x - 404) / 4) ** 2)) - 6 * Math.exp(-(((x - 421) / 7) ** 2)) - 3 * Math.exp(-(((x - 438) / 5) ** 2));
const popoY = x => 158 - Math.max(0, Math.min(19, 20.4 - Math.abs(x - POPO.x) * 0.5));
function renderVolcanoes() {
  const p = painter();
  const range = (topAt, rock, snow, snowRows, x0) => {
    for (let i = x0; i < W; i++) {
      const top = Math.round(topAt(i));
      if (top >= HZ) continue;
      for (let y = top; y < HZ + 2; y++) {
        const dep = y - top, sl = 158 - top < 8 ? 0 : snowRows + Math.round(Math.sin(i * 0.9));
        p.set(i, y, dep < sl ? dpick(snow, dep / Math.max(1, sl), i, y, 0.8) : dpick(rock, (dep - sl) / 10, i, y, 0.8));
      }
    }
  };
  range(popoY, ['#5a3a6a', '#523464', '#4a305e'], ['#b0aad4', '#9690c0', '#766ea2'], 6, 424);   // farther: hazier
  range(iztaY, ['#4a3060', '#432c5a', '#3c2854'], ['#c0bce0', '#a09cca', '#7e76ae'], 5, 386);
  return p.done();
}

/* ================= Far: the Sierra de Guadalupe's lit slopes, the SE hills, the Centro's rooftops and domes ================= */
const sierraY = x => 155 - 12 * Math.exp(-(((x - 18) / 22) ** 2)) - 33 * Math.exp(-(((x - 64) / 20) ** 2))
  - 26 * Math.exp(-(((x - 112) / 24) ** 2)) - 12 * Math.exp(-(((x - 160) / 22) ** 2)) + 2 * Math.sin(x * 0.21);
const seY = x => 156 - 5 * Math.exp(-(((x - 404) / 24) ** 2)) - 3 * Math.exp(-(((x - 470) / 30) ** 2));
const HILLW = ['#ffc070', '#ffb060', '#ffd89a', '#ffa25a', '#e4e8ff', '#c8f0d8'];
const WINC = ['#ffd27a', '#ffc462', '#ffb050', '#f59a44', '#ffe2a0', '#cfe0ff'];
const TILE = ['#f2c44a', '#3a78c8', '#e6e0d0', '#2e9a72'];            // Talavera-tiled church domes
function renderFar() {
  const p = painter(W, H, true), r = rng(21), wins = [], twink = [], beacons = [];
  // the Sierra de Guadalupe: dark crest, slopes covered in the lights of the northern colonias
  for (let i = 0; i < 206; i++) {
    const top = Math.round(sierraY(i));
    for (let y = top; y <= HZ; y++) p.set(i, y, y === top ? '#2c2448' : dpick(['#1c1734', '#211a3a', '#271e40'], (y - top) / 20, i, y));
    for (let y = top + 3; y <= HZ; y++) {
      const dens = 0.1 + 0.5 * smooth(top + 1, top + 16, y);                        // colonias climbing the slopes
      if (r() < dens) {
        const col = HILLW[Math.floor(r() * r() * HILLW.length)];
        p.set(i, y, mixHex(col, '#3a2a50', 0.12 + r() * 0.38));
        if (r() < 0.12) twink.push({ x: i, y, col, n: 3 + Math.floor(r() * 12), ph: r() });
      }
    }
  }
  for (const [ax, ah] of [[60, 9], [64, 12], [69, 8]]) {             // Cerro del Chiquihuite's antenna masts
    const top = Math.round(sierraY(ax));
    for (let y = top - ah; y < top; y++) p.set(ax, y, '#3a3050');
    beacons.push({ x: ax, y: top - ah - 1, ph: ax * 0.13 });
  }
  // the low hills to the south-east, lit to their crests
  for (let i = 360; i < W; i++) {
    const top = Math.round(seY(i));
    for (let y = top; y <= HZ + 4; y++) p.set(i, y, dpick(['#2a2144', '#231c3c'], (y - top) / 6, i, y));
    for (let y = top + 1; y <= HZ + 4; y++) if (r() < 0.32) {
      const col = HILLW[Math.floor(r() * r() * HILLW.length)];
      p.set(i, y, mixHex(col, '#40304e', 0.35 + r() * 0.3));
      if (r() < 0.1) twink.push({ x: i, y, col, n: 3 + Math.floor(r() * 10), ph: r() });
    }
  }
  // the Torre Insignia: a tall triangular prism with a pyramid crown (its carillon), a red lamp on top
  { const c = INS.cx;
    for (let y = INS.top + 7; y <= HZ + 2; y++) { p.set(c - 2, y, '#5a5a7e'); p.set(c - 1, y, '#46466a'); p.set(c, y, '#34345a'); p.set(c + 1, y, '#2a2a4c'); p.set(c + 2, y, '#222244'); }
    for (let y = INS.top; y < INS.top + 7; y++) { const hw = Math.floor((y - INS.top) / 2.4); for (let i = -hw; i <= hw; i++) p.set(c + i, y, i < 0 ? '#6a6c90' : '#3c3c60'); }
    for (let y = INS.top + 10; y <= HZ; y += 3) if (r() < 0.6) p.set(c - 1 + Math.floor(r() * 3), y, '#b8c4ff');
    beacons.push({ x: c, y: INS.top - 1, ph: 0.5 }); }
  // the city: rows of flat roofs with water tanks, domes and bell towers, nearer rows darker and lower
  const HROW = ['#3a2a52', '#30244a', '#291f42', '#221a3a', '#1c1632'];
  let bid = 0;
  const building = (bx, bw, top, bot, col, winP) => {
    p.id = bid++;
    const roof = mixHex(col, '#8a6a9a', 0.22);
    for (let y = top; y <= bot; y++) for (let i = bx; i < bx + bw; i++) p.set(i, y, col);
    for (let i = bx; i < bx + bw; i++) p.set(i, top, roof);
    if (r() < 0.45 && bw > 4) { const tx = bx + 1 + Math.floor(r() * (bw - 3)); p.set(tx, top - 1, '#141020'); p.set(tx + 1, top - 1, '#141020'); p.set(tx, top - 2, '#1c1628'); }
    for (let wy = top + 2; wy < bot; wy += 2) for (let wx = bx + 1; wx < bx + bw - 1; wx += 2)
      if (r() < winP) wins.push({ id: wins.length, x: wx, y: wy, b: p.id, n: 2 + Math.floor(r() * 8), p: 0.4 + r() * 0.5, off: r(), col: WINC[Math.floor(r() * r() * WINC.length)] });
  };
  const church = (cx, g, s) => {                      // a small domed church with a bell tower, floodlit
    p.id = -2;
    for (let y = g - 4 * s; y <= g; y++) for (let i = cx - 3 * s; i <= cx + 3 * s; i++) p.set(i, y, i < cx ? '#8a5a52' : '#6a4048');
    for (let j = 0; j < 3 * s; j++) { const hw = Math.round(2 * s * Math.sqrt(1 - (j / (3 * s)) ** 2)); for (let i = -hw; i <= hw; i++) p.set(cx + i, g - 4 * s - 1 - j, dpick(TILE, frac((i + j) * 0.37), cx + i, j)); }
    p.set(cx, g - 7 * s - 1, '#f2d27a');
    const tx = cx + 3 * s + 1;
    for (let y = g - 8 * s; y <= g; y++) { p.set(tx, y, '#9a6a5a'); p.set(tx + 1, y, '#7a4a48'); }
    p.set(tx, g - 7 * s + 1, '#ffd890'); p.set(tx, g - 8 * s - 1, '#c8a060');
  };
  for (let row = 0; row < 5; row++) {
    let bx = -Math.floor(r() * 6);
    const base = 158 + row * 5;
    while (bx < W) {
      const bw = 4 + Math.floor(r() * 9), top = base - 2 - Math.floor(r() * (4 + row));
      if (r() < 0.9) building(bx, bw, top, base + 6, HROW[row], 0.35 + row * 0.06);
      if (row < 3 && r() < 0.07) church(bx + bw + 3, base - 1, row < 2 ? 1 : 2);
      bx += bw;
    }
  }
  // the Metropolitan Cathedral's two bell towers far down Madero, just left of La Nacional
  p.id = -2;
  for (const tx of [285, 293]) {
    for (let y = 146; y <= 162; y++) for (let i = tx; i < tx + 4; i++) p.set(i, y, i === tx ? '#d8b088' : i < tx + 2 ? '#b88a6a' : '#8a6252');
    for (let y = 142; y < 146; y++) for (let i = tx + (y < 144 ? 1 : 0); i < tx + (y < 144 ? 3 : 4); i++) p.set(i, y, y === 142 ? '#e8c89a' : '#9a7a66');
    p.set(tx + 1, 141, '#f2d27a');
    p.set(tx + 1, 149, '#ffd890'); p.set(tx + 2, 149, '#ffb860'); p.set(tx + 1, 155, '#ffd890');
  }
  for (let y = 152; y <= 162; y++) for (let i = 289; i < 293; i++) p.set(i, y, '#9a7260');
  const vis = wins.filter(w => p.own[w.y * W + w.x] === w.b);
  vis.forEach((w, i) => { w.id = i; });
  return { c: p.done(), wins: vis, twink, beacons };
}

/* ================= Torre Latinoamericana: blue-green curtain wall, stepped crown, antenna mast ================= */
// Seen from the south-west: its west face (left, lit by the Centro's glow) and south face (right) meet at the corner
// column. 166 m to the roof, 182 m to the tip: it stands well over twice the height of Bellas Artes' dome.
const TLG = ['#4a7486', '#3a6070', '#2c4c5c', '#223e4e', '#1a3040', '#132432'];
function renderTL() {
  const p = painter(), r = rng(31), wins = [], c = TL.cx, corner = c - 2;
  const face = (x0, x1, y0, y1, left) => {
    for (let y = y0; y <= y1; y++) for (let i = x0; i <= x1; i++) {
      const fl = (y - y0) % 3, edge = i === x0 || i === x1;
      let k = left ? 2 : 3;
      if (fl === 0) k += 1;                                        // spandrel band between floors
      if (!left && i > x1 - 2) k += 1;
      if (left && i < x0 + 2) k -= 1;                              // the lit west edge
      if ((i - x0) % 3 === 1 && fl) k -= 1;                        // aluminium mullions catch the glow
      if (edge) k = left ? 1 : 4;
      p.set(i, y, TLG[clamp(k, 0, 5)]);
    }
  };
  face(c - TL.hw, corner, TL.top, 190, true);
  face(corner + 1, c + TL.hw, TL.top, 190, false);
  for (let y = TL.top; y <= 190; y++) p.set(corner, y, '#6a94a4');  // the corner column
  // office lights: a few floors still lit, in pairs of panes
  for (let y = TL.top + 5; y < 176; y += 3) for (let i = c - TL.hw + 1; i < c + TL.hw - 1; i += 3)
    if (r() < 0.2) wins.push({ id: wins.length, x: i, y: y + 1, n: 1 + Math.floor(r() * 5), p: 0.45 + r() * 0.45, off: r(), col: r() < 0.6 ? '#e8f0ff' : '#ffe0a0' });
  // the stepped crown: three setbacks (the observation floors glassier), a box and the lattice mast
  const step3 = [[TL.top - 6, TL.top - 1, 9], [TL.top - 12, TL.top - 7, 7], [TL.top - 17, TL.top - 13, 4]];
  for (const [y0, y1, hw] of step3) {
    for (let y = y0; y <= y1; y++) for (let i = c - hw; i <= c + hw; i++) {
      let col = i < corner ? TLG[1] : TLG[3];
      if (y === y0) col = '#8aa8b4';
      else if ((y - y0) % 2 === 0) col = i < corner ? TLG[2] : TLG[4];
      if (i === c - hw) col = TLG[0]; else if (i === c + hw) col = TLG[5];
      p.set(i, y, col);
    }
  }
  for (let y = TL.tip + 1; y < TL.top - 17; y++) {                  // the antenna mast
    const w = y > TL.top - 22 ? 1 : 0;
    for (let i = c - 1 - w; i <= c - 1 + w; i++) p.set(i, y, (y + i) % 2 ? '#6a7a8a' : '#3a4656');
  }
  return { c: p.done(), wins, crown: step3 };
}

/* ================= La Nacional: the 1932 Art Deco tower at the corner of Juárez and Eje Central ================= */
const LNP = ['#e6d6bc', '#cbb89c', '#a8967e', '#857464', '#5e5052', '#3e3440'];
function renderLN() {
  const p = painter(), r = rng(41), wins = [], c = LN.cx;
  const tiers = [[LN.top + 8, 192, LN.hw], [LN.top + 3, LN.top + 7, LN.hw - 4], [LN.top, LN.top + 2, LN.hw - 8], [LN.top - 4, LN.top - 1, 3]];
  for (const [y0, y1, hw] of tiers) for (let y = y0; y <= y1; y++) for (let i = c - hw; i <= c + hw; i++) {
    const k = i - (c - hw), right = i > c + hw - 3;
    let col = y === y0 ? LNP[0] : k % 4 === 0 ? LNP[1] : LNP[2];                     // vertical piers
    if (right) col = k % 4 === 0 ? LNP[3] : LNP[4];
    if (y > y0 && y < y1 && k % 4 !== 0 && (y - y0) % 4 === 2) col = right ? LNP[5] : '#4a3c46';   // window rows
    p.set(i, y, col);
  }
  for (let y = LN.top - 9; y < LN.top - 4; y++) p.set(c, y, '#8a7e76');            // flagpole
  for (let y = LN.top + 10; y < 186; y += 4) for (let i = c - LN.hw + 1; i < c + LN.hw - 3; i++)
    if ((i - (c - LN.hw)) % 4 !== 0 && r() < 0.18) wins.push({ id: wins.length, x: i, y, n: 1 + Math.floor(r() * 4), p: 0.5 + r() * 0.4, off: r(), col: r() < 0.7 ? '#ffd890' : '#e8eeff' });
  return { c: p.done(), wins };
}

/* ================= Palacio de Bellas Artes =================
   White Carrara marble, floodlit from below; the main façade on the avenue with its great arched portal, paired
   columns and the sculpted group over it; the triple-tiered dome clad in orange-and-gold tiles, crowned by the bronze
   eagle and the winged figures; two smaller tiled domes; the stage house rising behind on the north side; the west
   flank in shadow on the left; two of the bronze Pegasus groups on their pedestals at the plaza's corners. */
const MB = ['#fff8ea', '#f7e8cc', '#e8d2ae', '#cdb18c', '#a08670', '#6e5a5e', '#46384a'];
const DM = ['#fff2a8', '#ffd760', '#f7ae3a', '#e7872a', '#c2621e', '#8e4218', '#5a2a16'];
const GD = ['#fff0a0', '#e8b048'];
const BZ = ['#f0d49a', '#b08a56', '#6e5438', '#3a2e28', '#221a1a'];
const WL = ['#fff0c4', '#ffd48a', '#f0a454'];
const WD = '#4a3440';
const L3 = (() => { const v = [-0.2, 0.4, 0.9], n = Math.hypot(...v); return v.map(a => a / n); })();   // floodlights below, left
/** tiled dome: centre cx, half width hw, h rows standing on row `by` (exclusive); scale texture and gold meridian ribs */
function tileDome(p, cx, hw, by, h, amb = 0.34, ribs = 8) {
  const R = hw + 0.5, n = DM.length;
  for (let j = 0; j < h; j++) {
    const y = by - 1 - j, dy = (j + 0.5) / h;
    const rw = Math.min(hw, Math.floor(R * Math.sqrt(Math.max(0, 1 - dy * dy))));
    for (let i = cx - rw; i <= cx; i++) mset(p, cx, i, y, x => {
      const nx = (x - cx) / R, nz = Math.sqrt(Math.max(0, 1 - nx * nx - dy * dy));
      const lum = amb + (1 - amb) * Math.max(0, nx * L3[0] - dy * L3[1] + nz * L3[2]);
      let k = Math.floor((1 - lum) / (1 - amb) * (n - 1.2));
      if (((x - cx) + (j & 1) * 2) % 4 === 0 && j % 2 === 0) k++;                    // the tiles' scale rows
      const lon = Math.asin(clamp(nx / Math.max(0.2, Math.sqrt(1 - dy * dy)), -1, 1)) / (Math.PI / ribs);
      if (ribs && Math.abs(lon - Math.round(lon)) < 0.13 && j < h - 1) return lum > 0.62 ? GD[0] : GD[1];
      return DM[clamp(k, 0, n - 1)];
    });
  }
}
/** symmetric marble block cx±hw, rows y0..y1, lower rows brighter (floodlit), `cor`: a cornice on the top rows */
function marble(p, cx, hw, y0, y1, cor = true) {
  for (let y = y0; y <= y1; y++) run(p, cx, y, hw, x => {
    if (cor && y === y0) return MB[0];
    if (cor && y === y0 + 1) return MB[4];
    const d = Math.abs(x - cx) === hw ? 1 : 0;
    return MB[clamp((y > y1 - (y1 - y0) * 0.55 ? 1 : 2) + d, 0, 6)];
  });
}
const PEG_ROWS = [                                   // a winged horse rearing on its hind legs, facing right (bronze)
  '.W.W.V.....m......',
  '.WwWwWV...m#m.....',
  '.WwWwWWV..m###....',
  '..WwWwWV.m#####...',
  '..WwWwWV.###.##h..',
  '...WwWwV###h......',
  '....WwWV####m.....',
  '.....WWV#####hhh..',
  '.....m#######..h..',
  '....m#######...h#.',
  '...m######h.mh....',
  '.mm#####hh..h.....',
  '.#######h.........',
  '.##h#.#h#.........',
  'm#.##..##.........',
  '#..#....#.........',
  '#..##...#.........',
  '##.##..##.........',
];
// dark bronze against the white marble, its undersides and raised wings catching the plaza's floodlights
const PEG_KEY = { '#': BZ[3], m: BZ[2], h: BZ[1], W: BZ[1], w: BZ[2], V: BZ[0] };
const PEGS = [{ x: 162, dir: 1 }, { x: 262, dir: -1 }];   // pedestal centres at the plaza corners, rearing inward, hind legs clear of the niches
const BA_LAMPS = [176, 196, 228, 248];                                    // the plaza's Art Nouveau lamp posts
function renderBA() {
  const p = painter(), c = BA.cx, g = BA.base;
  // the stage house behind, on the north side (left from here), less lit
  for (let y = 108; y <= 132; y++) for (let i = 164; i <= 196; i++) {          // marble in shadow, a dark metal roof
    let col = y < 110 ? (y === 108 ? '#6a6272' : '#3a3244') : y === 110 ? '#8a767a' : i < 168 ? '#4a3c4c' : (i - 164) % 8 === 0 ? '#5e4e5a' : '#6a5a62';
    if (y > 124) col = mixHex(col, '#c8a88a', 0.15);
    p.set(i, y, col);
  }
  // main dome: drum with oculi, the broad lower tier, the upper drum and tier, the lantern and the bronze crown group
  marble(p, c, 26, 118, 131);
  for (let o = 0; o <= 24; o += 5) for (const s of [-1, 1]) {
    const x = c + s * o;
    p.set(x, 123, WL[1]); p.set(x, 124, WL[2]); p.set(x + s, 123, WL[1]); p.set(x + s, 124, WL[2]);
  }
  for (let o = 3; o <= 23; o += 5) for (let y = 120; y <= 130; y++) mset(p, c, c - o, y, MB[0]);   // drum columns
  tileDome(p, c, 25, 118, 13);
  marble(p, c, 13, 101, 105);
  for (let o = 2; o <= 11; o += 3) { mset(p, c, c - o, 103, WL[1]); mset(p, c, c - o, 104, WL[2]); }
  tileDome(p, c, 12, 101, 7, 0.36, 6);
  for (let y = 90; y <= 93; y++) run(p, c, y, 3, x => Math.abs(x - c) === 3 ? MB[2] : y === 90 ? MB[0] : WL[Math.abs(x - c) & 1]);
  for (const [dx, dy, col] of [[0, 85, BZ[1]], [0, 86, BZ[2]], [-1, 86, BZ[1]], [1, 86, BZ[1]], [0, 87, BZ[2]], [-1, 87, BZ[2]], [1, 87, BZ[2]],
    [-2, 87, BZ[1]], [2, 87, BZ[1]], [-3, 86, BZ[1]], [3, 86, BZ[1]], [-4, 85, BZ[0]], [4, 85, BZ[0]], [-3, 85, BZ[1]], [3, 85, BZ[1]],
    [0, 88, BZ[3]], [-1, 88, BZ[3]], [1, 88, BZ[3]], [0, 89, BZ[3]]]) p.set(c + dx, dy, col);    // the eagle, wings spread
  for (const s of [-1, 1]) for (const o of [2, 5]) {                // four winged figures holding hands round it
    p.set(c + s * o, 88, BZ[0]); p.set(c + s * o, 89, BZ[2]); p.set(c + s * (o + 1), 88, BZ[1]); p.set(c + s * (o + 1), 87, BZ[1]);
  }
  // the two smaller tiled domes over the front wings
  for (const s of [-1, 1]) {
    const x = c + s * 44;
    marble(p, x, 6, 124, 130);
    tileDome(p, x, 6, 124, 8, 0.3, 4);
    for (let y = 113; y <= 116; y++) run(p, x, y, 1, y === 113 ? MB[1] : WL[1]);
    p.set(x, 112, GD[1]); p.set(x, 111, GD[0]);
  }
  // the west flank in shadow, receding on the left
  for (let y = 128; y <= g; y++) for (let i = BA.x0; i < c - 60; i++) {
    let col = y === 128 ? MB[3] : y < 131 ? MB[5] : (i - BA.x0) % 4 === 1 && y > 134 && y < 164 && (y - 134) % 15 < 10 ? MB[6] : MB[5];
    if (y > 160) col = mixHex(col, '#f0c890', 0.18);
    p.set(i, y, col);
  }
  // the main façade: attic balustrade, cornice, two storeys of columns and windows, the plinth
  for (let y = 126; y <= 129; y++) run(p, c, y, 60, x => (y === 126 ? MB[1] : (x & 1) && y > 126 ? MB[4] : MB[2]));
  for (let y = 130; y <= 132; y++) run(p, c, y, 61, y === 130 ? MB[0] : y === 131 ? MB[1] : MB[4]);
  marble(p, c, 60, 133, 167, false);
  for (let y = 150; y <= 151; y++) run(p, c, y, 60, y === 150 ? MB[0] : MB[3]);
  const WOFF = [22, 27, 32, 37, 42, 47];
  for (const o of WOFF) for (const s of [-1, 1]) {
    const x = c + s * o;
    for (let y = 136; y <= 147; y++) for (const dx of [0, s]) p.set(x + dx, y, y === 136 ? MB[3] : y < 139 ? WL[2] : '#5a4048');
    for (let y = 155; y <= 166; y++) for (const dx of [0, s]) p.set(x + dx, y, y === 155 ? MB[3] : y < 158 ? WL[1] : y > 163 ? WL[2] : '#6a4a4a');
    for (let y = 133; y <= 167; y++) p.set(x - s * 2, y, y % 17 < 2 ? MB[1] : MB[0]);   // pilasters between the windows
  }
  // corner pavilions: raised attic with a sculpted crest, a tall arched niche
  for (const s of [-1, 1]) {
    const x = c + s * 55;
    marble(p, x, 5, 121, 129);
    for (let y = 117; y <= 120; y++) run(p, x, y, 3 - (120 - y) % 2, y === 117 ? MB[0] : MB[2]);
    for (let y = 138; y <= 165; y++) run(p, x, y, 1, y === 138 ? MB[3] : y < 142 ? WL[2] : '#5a4048');
    for (let y = 133; y <= 167; y++) { p.set(x - 4, y, MB[0]); p.set(x + 4, y, MB[3]); }
  }
  // the central pavilion: raised attic, the sculpted group over the arch, paired columns, the great portal
  marble(p, c, 18, 120, 129);
  // the sculpted group crowning the portal: a pyramid of marble figures, shadowed between them
  for (const [dx, y0] of [[0, 110], [-1, 111], [1, 111], [-2, 113], [2, 113], [-3, 114], [3, 114], [-4, 115], [4, 115], [-5, 116], [5, 116], [-6, 117], [6, 117], [-7, 118], [7, 118]])
    for (let y = y0; y < 121; y++) p.set(c + dx, y, y === y0 ? MB[0] : Math.abs(dx) % 2 ? MB[4] : y > 117 ? MB[2] : MB[1]);
  for (const s of [-1, 1]) { p.set(c + s * 3, 112, BZ[1]); p.set(c + s * 4, 113, BZ[1]); }      // bronze wings of the figures
  for (const o of [10, 11, 14, 15]) for (let y = 133; y <= 167; y++) mset(p, c, c - o, y, x => (x < c ? (o & 1 ? MB[0] : MB[1]) : (o & 1 ? MB[2] : MB[3])));
  for (let y = 134; y <= 167; y++) {
    const hw = y < 143 ? Math.round(Math.sqrt(Math.max(0, 64 - (143 - y) ** 2))) : 8;
    if (hw <= 0) continue;
    run(p, c, y, hw, x => {
      const k = Math.abs(x - c);
      if (k === hw) return MB[4];
      if (y < 146) return (k + y) % 3 === 0 ? '#c07a3a' : WL[0];                    // the stained glass fan
      if (k === 3 || k === 7 || y === 155) return '#6a4638';                         // door piers
      return y < 160 ? WL[1] : WL[2];
    });
  }
  for (let y = 168; y <= g; y++) run(p, c, y, 56 + (y - 168), x => ((y - 168) & 1 ? MB[2] : MB[1]));   // the steps
  // the plaza: lamp posts (their glow drawn per frame) and the Pegasus groups on their pedestals
  for (const lx of BA_LAMPS) { for (let y = 161; y <= 175; y++) p.set(lx, y, '#3a2e28'); p.set(lx - 1, 161, '#3a2e28'); p.set(lx + 1, 161, '#3a2e28'); }
  for (const pg of PEGS) {
    for (let y = 160; y <= 176; y++) run(p, pg.x, y, y < 162 ? 3 : 2, x => {
      const k = x - pg.x, hw = y < 162 ? 3 : 2;
      if (y === 160) return MB[0];
      if (y === 161 || y === 165) return MB[3];                                   // the cap's cornice and the die's moulding
      return k === -hw ? MB[2] : k < 0 ? MB[1] : k === hw ? MB[5] : k === 0 ? MB[2] : MB[4];
    });
    paintRows(p, PEG_ROWS, PEG_KEY, pg.dir > 0 ? pg.x - 6 : pg.x - 11, 160 - PEG_ROWS.length, pg.dir < 0);
  }
  for (let i = BA.x0; i <= BA.x1; i++) for (let y = 175; y <= 179; y++)       // the plaza's balustrade
    p.set(i, y, y === 175 ? MB[1] : y === 179 ? MB[5] : (i % 3 === 0 ? MB[2] : MB[4]));
  return p.done();
}

/* ================= Beyond the Alameda: Avenida Hidalgo's façades, a tezontle church, the park's trees ================= */
const BELL = { x: 73, y: 120 };                      // the bell in the church tower's upper opening
const TEZ = ['#9a4a3a', '#7e3a32', '#62302e', '#48262a'];               // red volcanic tezontle
const CHIL = ['#e8dcc4', '#c4b49a', '#9a8a76'];                           // grey chiluca stone trim
function renderMid() {
  const p = painter(W, H, true), r = rng(55), wins = [], lamps = [];
  let bid = 0;
  for (let bx = -2; bx < 136;) {                                          // Hidalgo's row of façades
    const bw = Math.min(7 + Math.floor(r() * 10), 140 - bx), top = 146 + Math.floor(r() * 12), col = mixHex('#2a2038', r() < 0.5 ? '#3a2430' : '#24283e', r() * 0.6);
    p.id = bid++;
    for (let y = top; y < 182; y++) for (let i = bx; i < bx + bw; i++) p.set(i, y, y === top ? mixHex(col, '#9a7a8a', 0.3) : col);
    for (let wy = top + 3; wy < 176; wy += 4) for (let wx = bx + 1; wx < bx + bw - 1; wx += 3)
      if (r() < 0.5) wins.push({ id: wins.length, x: wx, y: wy, b: p.id, n: 2 + Math.floor(r() * 7), p: 0.45 + r() * 0.45, off: r(), col: WINC[Math.floor(r() * r() * WINC.length)], w: 2 });
    bx += bw;
  }
  // the church: tezontle walls, a grey stone portal, a Talavera dome and a two-stage bell tower
  p.id = -2;
  const { cx, g } = CH;
  for (let y = 134; y <= g + 20; y++) for (let i = cx - 16; i <= cx + 12; i++) p.set(i, y, dpick(TEZ, 0.15 + 0.6 * (i - cx + 16) / 28 + (y < 136 ? -0.2 : 0), i, y, 0.6));
  for (let i = cx - 17; i <= cx + 13; i++) { p.set(i, 133, CHIL[0]); p.set(i, 134, CHIL[2]); }
  for (let y = 140; y <= g + 20; y++) for (let i = cx - 6; i <= cx + 2; i++) {                        // the portal
    const k = i - (cx - 6), arch = y < 146 ? Math.abs(i - cx + 2) > Math.round(Math.sqrt(Math.max(0, 9 - (146 - y) ** 2))) + 1 : false;
    p.set(i, y, k === 0 || k === 8 || y === 140 || arch ? CHIL[k < 4 ? 0 : 1] : y > 150 ? '#ffc070' : '#3a2230');
  }
  for (let y = 152; y <= 158; y++) for (let i = cx - 4; i <= cx; i++) p.set(i, y, (i + y) % 2 ? '#e89a4a' : '#ffc070');
  for (let j = 0; j < 9; j++) {                                           // drum + tiled dome behind the façade
    const hw = Math.round(9 * Math.sqrt(1 - (j / 9) ** 2));
    for (let i = -hw; i <= hw; i++) p.set(cx - 8 + i, 132 - j, j === 0 ? CHIL[1] : dpick(TILE, frac((i * 0.5 + j) * 0.31), cx - 8 + i, j, 0.4));
  }
  for (let y = 119; y <= 123; y++) { p.set(cx - 8, y, y === 119 ? '#f2d27a' : CHIL[1]); if (y > 120) { p.set(cx - 9, y, CHIL[0]); p.set(cx - 7, y, CHIL[2]); } }
  const tx = BELL.x;                                                      // the bell tower on the right
  for (let y = 112; y <= g + 20; y++) for (let i = tx - 5; i <= tx + 5; i++) {
    let col = dpick(TEZ, 0.2 + 0.6 * (i - tx + 5) / 10, i, y, 0.6);
    if (y === 128 || y === 112 || y === 113) col = CHIL[y === 113 ? 2 : 0];
    if (Math.abs(i - tx) <= 2 && ((y >= 115 && y <= 125) || (y >= 131 && y <= 138)))                 // two arched openings
      col = (y === 115 || y === 131) && Math.abs(i - tx) === 2 ? CHIL[1] : y < 126 ? '#2a1a26' : '#ffb060';
    if (Math.abs(i - tx) === 3 && y > 113 && y < 140) col = CHIL[1];
    p.set(i, y, col);
  }
  for (let y = 105; y < 112; y++) { const hw = y < 108 ? 1 : 3; for (let i = tx - hw; i <= tx + hw; i++) p.set(i, y, i < tx ? CHIL[0] : CHIL[2]); }
  p.set(tx, 103, '#f2d27a'); p.set(tx, 104, '#c89a50'); p.set(tx - 1, 103, '#c89a50'); p.set(tx + 1, 103, '#c89a50');
  // the Alameda's trees: ash and jacaranda crowns, dark, lit from beneath by the park lamps and the arches
  p.id = -3;
  const tree = (tx0, cy, rx, ry, lit) => {
    for (let y = Math.floor(cy - ry - 1); y <= Math.ceil(cy + ry); y++) for (let i = Math.floor(tx0 - rx - 1); i <= Math.ceil(tx0 + rx + 1); i++) {
      const dx = (i - tx0) / rx, dy = (y - cy) / ry, v = 1 - dx * dx - dy * dy + (bayer(i, y) - 0.5) * 0.5 + (r() - 0.5) * 0.3;
      if (v <= 0) continue;
      let col = v > 0.45 ? '#0c1a1c' : '#112424';
      if (dy < -0.3 && v < 0.55) col = dy < -0.6 ? '#2c4a3a' : '#1c3630';
      if (dy > 0.25 && lit && v < 0.7) col = dy > 0.6 ? '#6a5a2c' : '#3a3a26';
      p.set(i, y, col);
    }
  };
  for (let x = -6; x < 146; x += 11 + Math.floor(r() * 8)) {              // tall old ash crowns (lower before the church)
    const church = x > 34 && x < 86;
    tree(x, church ? 164 + r() * 3 : 150 + r() * 6, 9 + r() * 5, church ? 7 + r() * 2 : 11 + r() * 5, true);
  }
  for (let x = -4; x < 150; x += 12 + Math.floor(r() * 8)) tree(x, 170 + r() * 3, 8 + r() * 4, 6 + r() * 2, true);
  tree(146, 160, 6, 10, true);                                            // one crown overlapping the palace's west flank
  for (const lx of [14, 50, 86, 122]) { for (let y = 170; y < 181; y++) p.set(lx, y, '#2a2226'); p.set(lx, 169, '#fff0c0'); lamps.push({ x: lx, y: 169 }); }
  const vis = wins.filter(w => p.own[w.y * W + w.x] === w.b);
  vis.forEach((w, i) => { w.id = i; });
  return { c: p.done(), wins: vis, lamps };
}

/* ================= Eje Central's far lane: asphalt, the trolleybus wires on their poles ================= */
const POLES = [288, 364, 446];
function renderLane() {
  const p = painter();
  for (let i = 281; i < W; i++) {
    for (let y = 172; y <= 181; y++) p.set(i, y, y === 181 ? '#8a7c86' : dpick(['#2a2232', '#221c2a'], (y - 172) / 9, i, y));
    for (let y = 182; y <= 187; y++) p.set(i, y, y === 187 ? '#9a8c90' : dpick(['#4a3e48', '#3a3040'], (y - 182) / 5, i, y));
  }
  for (const x of POLES) for (let y = 160; y <= 181; y++) p.set(x, y, y < 163 ? '#5a5260' : '#2a2430');
  for (let i = 281; i < W; i++) { p.set(i, 163, (i & 1) ? '#4a4452' : '#3a3444'); p.set(i, 165, (i & 1) ? '#3a3444' : '#4a4452'); }
  return p.done();
}

/* ================= Rooftop billboard: a steel "espectacular" on the roof next door, lamps on its catwalk ================= */
const BB_FR = { x: 346, y: 118, w: 62, h: 70 };      // the frame sprite's box (scene px)
const BB_LAMPS = [BB.x + 8, BB.x + 25, BB.x + 42];   // lamp heads on the catwalk, pointing up at the face
const BB_TIRED = [[61.2, 62.4], [171.6, 172.3], [219.1, 219.6]];
const STEEL = ['#7a7a96', '#56566e', '#3a3a50', '#262638', '#181826'];
function renderBillboardFrame() {
  const p = painter(BB_FR.w, BB_FR.h), S = (x, y, col) => p.set(x - BB_FR.x, y - BB_FR.y, col);
  const x0 = BB.x - 1, x1 = BB.x + BB.w, y0 = BB.y - 1, y1 = BB.y + BB.h, bot = NB.roof;
  const LEGS = [x0 + 8, x1 - 9];
  for (const lx of LEGS) for (let y = y1 + 3; y < bot; y++) { S(lx, y, STEEL[2]); S(lx + 1, y, STEEL[3]); S(lx + 3, y, STEEL[3]); }
  for (const lx of LEGS) for (let y = y1 + 4; y < bot - 1; y += 5) { S(lx + 2, y, STEEL[2]); S(lx + 2, y + 2, STEEL[3]); }
  for (let i = LEGS[0] + 4; i < LEGS[1]; i++) {                         // X bracing between the legs
    const f = (i - LEGS[0] - 4) / (LEGS[1] - LEGS[0] - 4);
    S(i, Math.round(lerp(y1 + 6, bot - 3, f)), STEEL[3]); S(i, Math.round(lerp(bot - 3, y1 + 6, f)), STEEL[3]);
  }
  for (let i = x0 - 2; i <= x1 + 2; i++) { S(i, y1 + 1, (i & 1) ? '#7a7084' : '#5a5268'); S(i, y1 + 2, '#1c1a2a'); }   // catwalk
  for (let i = x0 - 2; i <= x1 + 2; i += 3) S(i, y1 - 0, STEEL[2]);
  for (let i = x0; i <= x1; i++) { S(i, y0, STEEL[1]); S(i, y0 - 1, STEEL[3]); }
  for (let y = y0; y < y1; y++) { S(x0, y, STEEL[2]); S(x1, y, STEEL[3]); S(x1 + 1, y, STEEL[4]); }
  for (const lx of BB_LAMPS) { S(lx - 1, y1 + 3, STEEL[1]); S(lx, y1 + 3, STEEL[0]); S(lx + 1, y1 + 3, STEEL[1]); S(lx, y1 + 4, STEEL[2]); }
  return p.done();
}
// the city's own ad: a panadería's pan de muerto, "fresh from the oven", on a Día de Muertos purple board
const AD_BG = ['#7a3ec8', '#6630b0', '#54269a', '#441e84', '#36166c'];
const AD_SUBS = ['RECIÉN', 'HORNEADO', ''];
const BUN = { x: 2, y: 7, w: 17, h: 13 };             // the loaf on the face
function renderAd(sub) {
  const p = painter(BB.w, BB.h);
  for (let y = 0; y < BB.h; y++) for (let i = 0; i < BB.w; i++) {
    let lit = 0;
    for (const lx of BB_LAMPS) lit = Math.max(lit, 1 - Math.hypot((i - (lx - BB.x)) / 12, (BB.h - y) / 9));
    p.set(i, y, dpick(AD_BG, 0.75 - 0.4 * y / BB.h - 0.55 * lit + 0.1 * Math.abs(i - 24) / 25, i, y));
  }
  const FLAG = ['#ff3d9a', '#ffb020', '#3ad6c8', '#b8f040', '#ff5a3a', '#ffe14a'];     // a string of papel picado
  for (let i = 0; i < BB.w; i++) p.set(i, 0, '#2a1048');
  for (let k = 0; k * 4 + 1 < BB.w; k++) for (let dx = 0; dx < 3; dx++) { p.set(k * 4 + 1 + dx, 1, FLAG[k % 6]); if (dx === 1 || k % 2) p.set(k * 4 + 1 + dx, 2, FLAG[k % 6]); }
  // the loaf: a sugared golden dome, its "bones" crossing over it, the little ball on top
  const { x: bx, y: by, w: bw, h: bh } = BUN, bcx = bx + (bw - 1) / 2;
  for (let y = by + 3; y < by + bh; y++) for (let i = bx; i < bx + bw; i++) {
    const dx = (i + 0.5 - (bcx + 0.5)) / (bw / 2), dy = (y + 0.5 - (by + bh - 1.5)) / (bh - 4);
    if (dx * dx + dy * dy > 1 && y < by + bh - 2) continue;
    if (y >= by + bh - 2 && Math.abs(dx) > 0.92) continue;
    let col = y >= by + bh - 2 ? '#8a4a1c' : dpick(['#ffd894', '#f0a650', '#d07a30', '#a0501c'], 0.2 + 0.45 * (dx + 1) / 2 + 0.35 * (dy + 1) / 2, i, y, 0.6);
    const ridge = Math.abs(i - bcx) < 0.6 || Math.abs(Math.abs(i - bcx) - 4.5 * Math.sqrt(Math.max(0, 1 - ((y - by - 3) / (bh - 4)) ** 2)) - 0.5) < 0.7;
    if (ridge && y < by + bh - 2) col = (i + y) % 2 ? '#b8621e' : '#ffcc78';
    if (!ridge && y < by + 8 && (i * 7 + y * 3) % 5 === 0) col = '#fff6e4';                        // sugar
    p.set(i, y, col);
  }
  for (const [dx, dy, col] of [[0, 0, '#ffd894'], [1, 0, '#f0a650'], [-1, 1, '#ffd894'], [0, 1, '#f0a650'], [1, 1, '#d07a30'], [2, 1, '#a0501c'], [0, 2, '#d07a30'], [1, 2, '#a0501c']])
    p.set(Math.round(bcx) + dx - 1, by + 1 + dy, col);
  const tx = 21, sh = '#240a3c';
  for (const [s, y, col] of [['PAN DE', 4, '#fff2d8'], ['MUERTO', 10, '#ffb02a']]) { ptext(p, s, tx + 2, y + 1, () => sh); ptext(p, s, tx + 1, y, () => col); }
  if (sub) { const w = sub.length * 4 - 1, sx = 19 + Math.round((31 - w) / 2); ptext(p, sub, sx, 16, () => '#ff7ac4'); }
  return p.done();
}
function renderPoolMask() {                          // the middle lamp's light on the face, taken away while it stutters
  const [c, x] = makeCanvas(BB.w, BB.h), img = x.createImageData(BB.w, BB.h), d = img.data, cx = BB_LAMPS[1] - BB.x;
  for (let y = 0; y < BB.h; y++) for (let i = 0; i < BB.w; i++) {
    const v = clamp(1.2 - Math.hypot((i - cx) / 13, (BB.h + 2 - y) / 16), 0, 1), q = Math.floor(v * 3 + bayer(i, y)) / 3;
    if (q <= 0) continue;
    const o = (y * BB.w + i) * 4; d[o] = 14; d[o + 1] = 4; d[o + 2] = 30; d[o + 3] = Math.round(Math.min(1, q) * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Next door: a lower 1950s block on our side of Juárez, water tanks on its roof, a HOTEL neon ================= */
const NEON = { x: 350, y: 191 };
function renderNeighbour() {
  const p = painter(), r = rng(77), wins = [];
  for (let y = NB.roof; y < LEDGE + 2; y++) for (let i = NB.x0; i < W; i++) {
    let col = y === NB.roof ? '#6a5a6a' : y === NB.roof + 1 ? '#2a2030' : (y - NB.roof - 2) % 9 === 0 ? '#3a3040' : '#2a2236';
    if (i === NB.x0) col = '#4a3e4c';
    p.set(i, y, col);
  }
  for (let wy = NB.roof + 4; wy < LEDGE; wy += 9) for (let wx = NB.x0 + 12; wx < W - 2; wx += 7)
    for (let y = wy; y < wy + 5; y++) for (let i = wx; i < wx + 4; i++) p.set(i, y, '#1c1626');
  for (let wy = NB.roof + 4; wy < LEDGE; wy += 9) for (let wx = NB.x0 + 12; wx < W - 2; wx += 7)
    if (r() < 0.4) wins.push({ x: wx, y: wy, n: 1 + Math.floor(r() * 4), off: r(), p: 0.55 + r() * 0.4, col: r() < 0.7 ? '#ffc880' : '#b8d0ff', id: wins.length });
  for (const tx of [362, 372]) for (let y = NB.roof - 6; y < NB.roof; y++) for (let i = tx; i < tx + 5; i++)     // tinacos
    p.set(i, y, y === NB.roof - 6 ? '#3a3a44' : i === tx ? '#2e2e38' : '#16161e');
  for (let y = NB.roof - 12; y < NB.roof; y++) p.set(381, y, '#3a3444');                                  // TV antenna
  for (const [dx, dy] of [[-3, -11], [-2, -11], [-1, -11], [1, -11], [2, -11], [3, -11], [-2, -8], [-1, -8], [1, -8], [2, -8]]) p.set(381 + dx, NB.roof + dy, '#3a3444');
  for (let y = NEON.y - 1; y < NEON.y + 30; y++) for (let i = NEON.x - 2; i <= NEON.x + 4; i++)
    p.set(i, y, i === NEON.x - 2 || i === NEON.x + 4 || y === NEON.y - 1 || y === NEON.y + 29 ? '#5a4450' : '#1a1018');
  return { c: p.done(), wins };
}
function renderNeonText(on) {                        // the vertical sign's letters, lit or dark
  const p = painter(3, 29), col = on ? '#ff4a5a' : '#5a2a34';
  [...'HOTEL'].forEach((ch, k) => ptext(p, ch, 0, k * 6, () => col));
  return p.done();
}

/* ================= The sidewalk along the Alameda: marigold arches, park lamps, vendors' carts ================= */
const ARCH_X = [2, 22, 42, 62, 82, 102, 122, 142];    // arch posts: marigold arches over the Alameda's walk
const CEMP = ['#ffc21a', '#ff9d0a', '#f07c00', '#c85a00'];                // cempasúchil
const TERC = ['#e0287a', '#b01860'];                                      // terciopelo (cockscomb), magenta
const ELOTE = { x: 186 }, TAMAL = { x: 236 }, GLOBO = { x: 272 }, ORGAN = { x: 106 };
function marigoldBlob(p, x, y, k) {
  const pal = k % 7 === 3 ? TERC : CEMP;
  p.set(x, y, pal[0]); p.set(x + 1, y, pal[1 % pal.length]); p.set(x, y + 1, pal[(k & 1) + 1 < pal.length ? (k & 1) + 1 : 1]);
  p.set(x + 1, y + 1, pal[pal.length - 1]);
}
function renderWalk() {
  const p = painter(), r = rng(91), glow = [];
  for (let i = 0; i < NB.x0; i++) for (let y = WALK.y0; y < ST.y0; y++) {
    const L = Math.max(0, ...[14, 50, 86, 122, 176, 196, 228, 248].map(lx => 1 - Math.abs(i - lx) / 22));
    let col = dpick(['#2a2230', '#3a2e38', '#54423e', '#6e5646'], 0.15 + L * 0.8 - (y - WALK.y0) * 0.02, i, y, 0.7);
    if (y === ST.y0 - 1) col = '#a0908c';
    else if (y === ST.y0 - 2) col = '#6a5a5c';
    p.set(i, y, col);
  }
  // marigold arches: posts wound with flowers and arcs of flower heads with magenta beads
  for (let k = 0; k + 1 < ARCH_X.length; k++) {
    const a = ARCH_X[k], b = ARCH_X[k + 1], m = (a + b) / 2, rad = (b - a) / 2;
    for (let x = a; x <= b; x += 1) {
      const y = Math.round(172 - Math.sqrt(Math.max(0, rad * rad - (x - m) ** 2)) * 0.9);
      if ((x - a) % 2 === 0) { marigoldBlob(p, x, y - 1, x + k); glow.push({ x, y }); }
    }
  }
  for (const a of ARCH_X) for (let y = 170; y < 187; y += 2) marigoldBlob(p, a - 1 + (y & 2 ? 1 : 0), y, y + a);
  // the elote cart: a steel pot, a small awning, cobs under a lamp
  { const x = ELOTE.x;
    for (let y = 179; y <= 186; y++) for (let i = x - 5; i <= x + 5; i++) p.set(i, y, y === 179 ? '#c8c8d0' : i < x ? '#8a8a96' : '#5a5a66');
    for (const wx of [x - 4, x + 4]) { p.set(wx, 187, '#1a1a22'); p.set(wx - 1, 187, '#2a2a32'); }
    for (let i = x - 7; i <= x + 7; i++) { p.set(i, 170, (i >> 1) & 1 ? '#e8e8f0' : '#e03040'); p.set(i, 171, (i >> 1) & 1 ? '#c8c8d0' : '#a82030'); }
    for (let y = 172; y < 179; y++) p.set(x + 6, y, '#6a6a70');
    for (let i = x - 3; i <= x + 1; i++) { p.set(i, 177, '#f2d060'); p.set(i, 178, '#d8a830'); }
    p.set(x - 1, 172, '#fff2c0'); }
  // the tamales tricycle: the tall steamer pot on the back
  { const x = TAMAL.x;
    for (let y = 173; y <= 183; y++) for (let i = x; i <= x + 6; i++) p.set(i, y, y === 173 ? '#d8d8e0' : i < x + 3 ? '#a8a8b4' : '#707080');
    for (let i = x - 1; i <= x + 7; i++) p.set(i, 184, '#3a3a44');
    for (const [wx, wy] of [[x, 186], [x + 6, 186], [x - 7, 186]]) for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) p.set(wx + dx, wy + dy, '#1c1c24');
    for (let k = 0; k < 7; k++) p.set(x - 7 + k, 184 - (k >> 1), '#3a3a44');
    for (let y = 176; y <= 184; y++) p.set(x - 6, y, '#2a2a34'); }
  // the organillero's barrel organ on its stick, and the balloon seller's pole (both people drawn per frame)
  { const x = ORGAN.x;
    for (let y = 176; y <= 181; y++) for (let i = x - 3; i <= x + 3; i++) p.set(i, y, y === 176 ? '#c8904a' : i === x - 3 || i === x + 3 ? '#5a3418' : (i + y) % 3 ? '#8a5428' : '#e0b060');
    for (let y = 182; y <= 187; y++) p.set(x, y, '#3a2414'); }
  for (let y = 172; y <= 187; y++) p.set(GLOBO.x, y, '#8a8a90');
  return { c: p.done(), glow };
}

/* ================= Avenida Juárez: asphalt, lane marks, the crossing at the corner ================= */
// The crossing in front of La Nacional runs away from us across the avenue: its stripes lie along the traffic, stacked
// in depth, thin and close at the far curb, thicker and wider apart near us, the whole band a little wider near us.
const XING = { cx: 311, hw: 11 };
const XING_ROWS = [[190, 1], [193, 1], [196, 1], [199, 1], [202, 2], [206, 2], [210, 2], [214, 2], [218, 2], [222, 2], [226, 2]];
function renderStreet() {
  const p = painter(), stripe = new Int8Array(H);
  for (const [y, h] of XING_ROWS) for (let j = 0; j < h; j++) stripe[y + j] = 1;
  for (let y = ST.y0; y < ST.y1; y++) {
    const d = (y - ST.y0) / (ST.y1 - ST.y0), cx = XING.cx + d * 2, hw = XING.hw + d * 3;
    for (let i = 0; i < W; i++) {
      const L = 0.25 * Math.max(0, ...[14, 50, 86, 122, 176, 228, 310].map(lx => 1 - Math.hypot(i - lx, (y - ST.y0) * 2.2) / 34));
      let col = dpick(['#0c0a12', '#110e17', '#16121c', '#1e1823', '#2a202a'], 0.18 + L * 1.7 - (y - ST.y0) * 0.003, i, y, 0.8);
      const inX = Math.abs(i - cx) <= hw;
      if ((y === 203 || y === 213) && i % 12 < 6 && Math.abs(i - cx) > hw + 4) col = y === 203 ? '#5a5260' : '#4a4452';
      if (inX && stripe[y]) {                                                     // worn white paint, brighter near us
        const paint = dpick(['#625a6c', '#7a7284', '#928a9c'], 0.3 + d * 0.5 + L * 1.2, i, y, 0.3);
        col = hash(i, y, 77) < 0.1 ? mixHex(paint, col, 0.6) : paint;
      }
      p.set(i, y, col);
    }
  }
  return p.done();
}

/* ================= Vehicles seen side-on (facing right; flipped for the westbound lane) ================= */
// h = headlight, T = taillight; R/L the roof and the lit top surfaces, c/C the body and its shaded lower half, g/G glass
// and its glint, p the pillars, k/o tyres and hubcaps, d the shadow under the body on the road (the last row sits on the
// lane's wheel row). Pink-and-white taxis, vochos, sedans, an ambulance.
const TAXI_ROWS = [
  '.........aaa.........',
  '.......WWWWWWW.......',
  '......WgGgpgGgW......',
  '.....WgGggpgGggW.....',
  '.QQQQLLLLLLLLLLLQQQQ.',
  'TPPPPwwwwwwwwwwwPPPPh',
  'uPPkkkvvvvvvvvvkkkPPu',
  '.ddkokdddddddddkokdd.',
  '.ddkkkdddddddddkkkdd.',
];
const VOCHO_ROWS = [                                  // the round-backed vocho
  '......RRRRR......',
  '....RRgGpgGRR....',
  '...RgGggpggGgR...',
  '..LLLLLLLLLLLLLL.',
  '.Tccccccccccccch.',
  'ucckkkCCCCCkkkccu',
  '.ddkokdddddkokdd.',
  '.ddkkkdddddkkkdd.',
];
const SEDAN_ROWS = [
  '.......RRRRRRR.......',
  '......cgGgpgGgc......',
  '.....cgGggpgGggc.....',
  '.LLLLLLLLLLLLLLLLLLL.',
  'Tccccccccccccccccccch',
  'uCCkkkCCCCCCCCCkkkCCu',
  '.ddkokdddddddddkokdd.',
  '.ddkkkdddddddddkkkdd.',
];
const AMBU_ROWS = [                                   // a white van, red stripe and cross, the light bar on its roof
  '...ssssssss.............',
  '.RRRRRRRRRRRRRRRRRR.....',
  '.wwwwwwwxwwwwwwwwwgG....',
  '.wwwwwwxxxwwwwwwwpggGw..',
  '.wwwwwwwxwwwwwwwwpgggLL.',
  '.rrrrrrrrrrrrrrrrrrrrrr.',
  'Twwwwwwwwwwwwwwwwwwwwwwh',
  'uvvvkkkvvvvvvvvvvkkkvvvu',
  '.ddkokdddddddddddkokddd.',
  '.ddkkkdddddddddddkkkddd.',
];
const CAR_COLS = ['#c8c0a8', '#7ab0d8', '#d84a3a', '#5a8a5a', '#e8d070', '#8a8a96', '#3a4a7a', '#f0f0f0'];
function vehicle(rows, key) { const c = sprite(rows, key); return [c, flipCanvas(c)]; }
// tyres a shade above the asphalt with silver hubcaps, so the wheels read against the dark road
const vkey = (body, extra = {}) => ({ c: body, R: mixHex(body, '#fff6e8', 0.3), L: mixHex(body, '#fff6e8', 0.18), C: mixHex(body, '#0c0a14', 0.4),
  g: '#22283e', G: '#4e5c80', p: '#141220', d: '#050308', k: '#3a3542', o: '#b8b4c4', u: '#6a6672', h: '#fff6d8', T: '#ff3a3a', ...extra });
const TAXI_PINK = '#ff3d8e';
const TROLLEY_ROWS = [                                // the blue-and-white Eje Central trolleybus
  '..bbbbbbbbbbbbbbbbbbbb..',
  '.bwwwwwwwwwwwwwwwwwwwwb.',
  '.bllwllwllwllwllwllwggb.',
  '.bllwllwllwllwllwllwggb.',
  '.bwwwwwwwwwwwwwwwwwwwwwh',
  'Tbbbbbbbbbbbbbbbbbbbbbbb',
  '.ddddddddddddddddddddddd',
  '...kk..............kk...',
];

/* ================= People on the Alameda's walk (feet on the last row), two walking frames ================= */
function walker(kind, a, b) {
  const faceRow = kind === 'catrina' ? ['.hfh.', 'hhhhh', '.kwk.', '.www.'] : kind === 'catrin' ? ['.hhh.', '.hhh.', 'hhhhh', '.kwk.', '.www.']
    : kind === 'kid' ? ['.kwk.', '.www.'] : ['.hhh.', '.fff.', '.fff.'];
  const body = kind === 'kid' ? ['.ddd.', '.ddd.'] : kind === 'catrina' ? ['.ddd.', 'ddddd', 'ddddd', '.ddd.'] : ['.ddd.', 'ddWdd', '.ddd.', '.ppp.'];
  const rows0 = [...faceRow, ...body, '.l.l.'], rows1 = [...faceRow, ...body, '..l..'];
  const key = { h: a, f: kind === 'person' ? '#c8906a' : '#ff5a9a', w: '#f4eee6', k: '#1a1418', d: b, W: '#f4eee6', p: '#2a2432', l: '#1a161c' };
  if (kind === 'person') { key.f = '#c8906a'; key.h = '#2a1a14'; }
  return [sprite(rows0, key), sprite(rows1, key)];
}
const WALKER_KINDS = [
  ['catrina', '#3a1a50', '#8a2a9a'], ['catrina', '#1a1420', '#c02040'], ['catrina', '#4a1030', '#e8a020'], ['catrin', '#141018', '#1c1822'],
  ['person', '#000', '#4a6a8a'], ['person', '#000', '#8a4a3a'], ['person', '#000', '#5a5a3a'], ['kid', '#000', '#e05a2a'], ['catrin', '#2a1030', '#3a2440'],
];

/* ================= The terrace: parapet, hot chocolate, pan de muerto, sugar skulls, tin lantern, the ofrenda ================= */
const CHOCO = { x: 46, y: 220 };                      // the clay cup of hot chocolate
const LANT = { x: 337 };                              // a punched-tin star lantern on the ledge, clear of the crossing
const SKULLS = [212, 225, 238, 251, 264, 277, 290];   // the sugar-skull row
const CANDLES = [                                    // standing on row `base`, `h` tall (flames drawn per frame)
  { x: 101, base: LEDGE - 1, h: 8, glass: true }, { x: 219, base: LEDGE - 1, h: 6 }, { x: 258, base: LEDGE - 1, h: 6 }, { x: 297, base: LEDGE - 1, h: 6 },
  { x: 408, base: 189, h: 7 }, { x: 452, base: 189, h: 7 }, { x: 410, base: 203, h: 6 }, { x: 450, base: 203, h: 6 },     // the ofrenda
  { x: 392, base: 217, h: 10, glass: true }, { x: 468, base: 217, h: 10, glass: true }, { x: 424, base: 217, h: 5 }, { x: 437, base: 217, h: 5 },
];
const COPAL = { x: 430, y: 212 };
const SKULL_ROWS = [
  '.sssss.',
  'sssssss',
  'sEEsEEs',
  'sEEsEEs',
  'sssnsss',
  '.ststs.',
  '..sss..',
];
const SK_COLS = [['#fff4f8', '#ff3d9a'], ['#f4fbff', '#2ab8e8'], ['#fffbe8', '#ffb020'], ['#f8fff0', '#48c850'], ['#fff4f8', '#8338ec'], ['#fff8f0', '#ff5a3a'], ['#f4fbff', '#ff3d9a']];
function candle(p, x, y, h, glass) {                  // a candle standing on row y (flame drawn per frame above y - h)
  for (let j = 0; j < h; j++) for (let i = -1; i <= 1; i++) p.set(x + i, y - j, glass ? (i < 0 ? '#ffd8a0' : i ? '#c88a50' : '#fff0c8') : i < 0 ? '#fff4dc' : i ? '#d8c8a8' : '#f4e8cc');
  p.set(x, y - h, '#3a2a20');
}
function renderForeground() {
  const p = painter(), r = rng(33);
  const lightAt = (i, y) => clamp(0.3 + 0.7 * Math.max(0, 1 - Math.hypot(i - OF.cx, (y - 214) * 1.3) / 120)
    + 0.35 * Math.max(0, 1 - Math.hypot(i - LANT.x, y - 220) / 60) + 0.3 * Math.max(0, 1 - Math.hypot(i - 70, y - 222) / 80), 0, 1);
  for (let i = 0; i < W; i++) {                                           // the parapet's stone coping
    const L = lightAt(i, LEDGE);
    p.set(i, LEDGE, dpick(['#6a4a3e', '#9a7056', '#c89a70'], L * 0.9 + r() * 0.12, i, LEDGE));
    p.set(i, LEDGE + 1, dpick(['#4a322e', '#7a5644'], L, i, LEDGE + 1));
    p.set(i, LEDGE + 2, '#2e1e22'); p.set(i, LEDGE + 3, '#1c1216');
  }
  const TV = { b: '#1e3c9a', l: '#dde4f4', y: '#f0b828', k: '#12204e' };  // a band of Talavera tiles on the ochre wall
  for (let y = LEDGE + 4; y < H; y++) for (let i = 0; i < W; i++) {
    const L = lightAt(i, y), ty = y - (LEDGE + 8);
    let col;
    if (ty >= 0 && ty < 10) {
      const lx = ((i % 10) + 10) % 10, dx = Math.abs(lx - 4.5), dy = Math.abs(ty - 4.5);
      let k = 'l';
      if (lx === 0 || ty === 0) k = 'k';
      else if (dx + dy < 1.6) k = 'y';
      else if (Math.abs(dx - dy) < 0.7 && dx + dy < 5.5) k = 'b';
      else if (dx > 3.6 && dy > 3.6) k = 'b';
      col = mixHex(TV[k], '#120a10', clamp(0.9 - L * 0.95, 0.1, 0.75));
    } else col = dpick(['#1e1014', '#3a1e1a', '#5a3022', '#7a4428'], L * (ty < 0 ? 1 : 0.75) - (y > 256 ? 0.2 : 0), i, y);
    p.set(i, y, col);
  }
  // a clay pot of cempasúchil at the left end
  for (let y = 212; y < LEDGE; y++) {
    const hw = y < 214 ? 9 : Math.round(8 - (y - 214) * 0.22);
    for (let i = 19 - hw; i <= 19 + hw; i++) p.set(i, y, y < 214 ? '#d8844a' : dpick(['#e8905a', '#c0683a', '#8a4428', '#5a2a1c'], 0.1 + 0.85 * (i - 19 + hw) / (2 * hw + 1), i, y, 0.6));
  }
  for (let y = 196; y < 213; y++) for (let i = 7; i <= 31; i++) {                         // a dome of cempasúchil heads
    const dx = (i - 19) / 12.5, dy = (y - 212) / 16;
    if (dx * dx + dy * dy > 1) continue;
    const cell = ((i >> 1) * 7 + (y >> 1) * 13) % 5, rim = (i & 1) + (y & 1);
    p.set(i, y, rim === 2 ? CEMP[3] : cell === 0 ? CEMP[0] : cell === 3 && y < 206 ? '#ffe070' : CEMP[1 + (cell & 1)]);
  }
  for (const [lx, ly] of [[8, 211], [30, 210], [12, 205], [27, 204]]) { p.set(lx, ly, '#3a6a30'); p.set(lx + (lx < 19 ? -1 : 1), ly + 1, '#2a5028'); }
  // the cup of hot chocolate (a clay jarrito on a saucer), the molinillo beside it, pan de muerto on a plate
  { const { x, y } = CHOCO;
    for (let i = x - 7; i <= x + 7; i++) { p.set(i, LEDGE - 1, i < x ? '#e8d0b0' : '#b89878'); if (Math.abs(i - x) < 6) p.set(i, LEDGE - 2, '#f4e4c8'); }
    for (let j = 0; j < 6; j++) for (let i = x - 4; i <= x + 4; i++) {
      const u = (i - x + 4) / 8;
      p.set(i, y + j, j === 0 ? (Math.abs(i - x) < 4 ? '#6a3418' : '#c8784a') : u < 0.25 ? '#e89a64' : u < 0.6 ? '#c8703e' : '#8a4428');
    }
    for (let i = x - 3; i <= x + 3; i++) p.set(i, y, (i & 1) ? '#8a4a24' : '#a86030');                       // the chocolate's froth
    for (const [dx, dy] of [[5, 1], [6, 2], [6, 3], [5, 4]]) p.set(x + dx, y + dy, '#a85a32');                  // handle
    for (let k = 0; k < 15; k++) { p.set(x + 10 + k, LEDGE - 3 + Math.round(k / 8), k < 9 ? '#b87a44' : '#8a5a30'); if (k > 9) p.set(x + 10 + k, LEDGE - 2 + Math.round(k / 8) - 1, '#6a4020'); }
    for (const rx of [x + 20, x + 23]) { p.set(rx, LEDGE - 4, '#d89a5a'); p.set(rx, LEDGE - 2, '#8a5a30'); }
    const px0 = 84;
    for (let i = px0 - 9; i <= px0 + 9; i++) { p.set(i, LEDGE - 1, i < px0 ? '#eef0f8' : '#b8bcd0'); if (Math.abs(i - px0) < 8) p.set(i, LEDGE - 2, '#dde0ec'); }
    for (let y2 = 217; y2 < LEDGE - 2; y2++) for (let i = px0 - 7; i <= px0 + 7; i++) {
      const dx = (i - px0) / 7.5, dy = (y2 - (LEDGE - 2)) / 9;
      if (dx * dx + dy * dy > 1) continue;
      let col = dpick(['#ffd894', '#f0a650', '#d07a30', '#a0501c'], 0.25 + 0.4 * (dx + 1) / 2 + 0.25 * (1 + dy), i, y2, 0.6);
      if (Math.abs(i - px0) < 1 || Math.abs(Math.abs(i - px0) - 4 * Math.sqrt(Math.max(0, 1 - ((y2 - 217) / 9) ** 2))) < 0.8) col = (i + y2) % 2 ? '#b8621e' : '#ffcc78';
      else if ((i * 5 + y2 * 3) % 4 === 0 && y2 < 222) col = '#fff4e0';
      p.set(i, y2, col);
    }
    p.set(px0, 215, '#f0a650'); p.set(px0 - 1, 216, '#ffd894'); p.set(px0, 216, '#d07a30'); p.set(px0 + 1, 216, '#a0501c'); }
  for (const c of CANDLES) if (c.x < OF.x0) candle(p, c.x, c.base, c.h, !!c.glass);
  // the sarape the xolo sleeps on: Saltillo stripes
  const SAR = ['#c8283a', '#f08a1a', '#f4d040', '#2a9a6a', '#2a4ab0', '#8a2a9a', '#f4ecdc', '#1a1418'];
  for (let y = 219; y < LEDGE; y++) for (let i = 140; i <= 198; i++) {
    const s = SAR[((i >> 1) + (y > 224 ? 3 : 0)) % SAR.length];
    p.set(i, y, y === 219 ? mixHex(s, '#fff0d0', 0.3) : y > 225 ? mixHex(s, '#100808', 0.4) : s);
  }
  for (let i = 138; i <= 200; i += 2) p.set(i, LEDGE - 1 + (i % 4 ? 1 : 0), '#e8d8b8');                          // fringe
  // the sugar skulls, each with its colour of icing and foil eyes
  SKULLS.forEach((sx, k) => {
    const [base, ice] = SK_COLS[k];
    paintRows(p, SKULL_ROWS, { s: base, E: k % 2 ? '#2ab8e8' : '#ffd23a', n: ice, t: ice }, sx - 3, LEDGE - 7);
    p.set(sx - 3, LEDGE - 6, ice); p.set(sx + 3, LEDGE - 6, ice); p.set(sx, LEDGE - 7, ice);
  });
  for (const mx of [231, 244, 270, 283]) for (const [dx, dy] of [[0, 0], [1, 0], [0, 1], [1, 1]]) p.set(mx + dx, LEDGE - 2 + dy - 1, CEMP[(dx + dy + mx) % 3]);
  // the punched-tin star lantern (its candle drawn per frame)
  { const lx = LANT.x;
    for (let y = 206; y < LEDGE; y++) {
      const hw = y < 210 ? y - 205 : y < 224 ? 5 : 5 - (y - 224);
      for (let i = lx - hw; i <= lx + hw; i++) {
        const pin = y > 209 && y < 224 && (i + y) % 3 === 0 && Math.abs(i - lx) < 5;
        p.set(i, y, pin ? '#ffe0a0' : i < lx ? '#b8b0b8' : i === lx ? '#8a8490' : '#5a5462');
      }
    }
    for (let y = 202; y < 206; y++) p.set(lx, y, '#8a8490'); p.set(lx - 1, 202, '#8a8490'); p.set(lx + 1, 202, '#8a8490'); }
  renderOfrenda(p, r);
  return p.done();
}
/** the ofrenda: three tiers draped purple, orange and magenta under papel picado valances, a marigold arch over it */
function renderOfrenda(p, r) {
  const { cx } = OF, TIERS = [[190, 24, '#6a2ab8', '#4a1a8a'], [204, 34, '#e06a10', '#a84a0a'], [218, 42, '#c01e6a', '#8a1450']];
  const VAL = ['#ff3d9a', '#ffb020', '#3ad6c8', '#b8f040', '#8338ec', '#ffe14a'];
  for (let y = 218; y < H; y++) for (let i = cx - 44; i <= cx + 44; i++)                  // the table skirt
    p.set(i, y, dpick(['#8a7898', '#6a5a82', '#4a3c66', '#30264a', '#221a38'], 0.25 + (y - 218) / 60 + (Math.abs(i - cx) > 40 ? 0.2 : 0), i, y, 0.6));
  for (let y = 234; y < H; y += 12) for (let i = cx - 44; i <= cx + 44; i++) {             // cut-paper motifs on the skirt
    const lx = ((i - cx + 44) % 12 + 12) % 12;
    if ((lx === 5 || lx === 6) || (lx > 3 && lx < 8 && ((y + 1) & 1))) p.set(i, y + (lx === 5 || lx === 6 ? 1 : 0), '#6a2ab8');
  }
  for (const [top, hw, cloth, dark] of TIERS) {
    for (let y = top; y < top + 14 && y < H; y++) for (let i = cx - hw; i <= cx + hw; i++)
      p.set(i, y, y === top ? mixHex(cloth, '#ffffff', 0.35) : dpick([cloth, dark], (y - top) / 14 + (Math.abs(i - cx) > hw - 3 ? 0.4 : 0), i, y, 0.6));
    for (let i = cx - hw; i <= cx + hw; i++) {                                              // cut-paper valance
      const k = Math.floor((i - cx + hw) / 6), col = VAL[k % VAL.length], lx = (i - cx + hw) % 6;
      for (let y = top + 1; y < top + 6; y++) if (!(y === top + 3 && (lx === 2 || lx === 3)) && !(y === top + 5 && lx % 2)) p.set(i, y, lx === 5 ? '#2a1030' : col);
    }
  }
  // tier three: the family's photographs in their frames, candles at the ends
  for (const [fx, face] of [[414, '#c8a888'], [426, '#b89878'], [438, '#d0b090']]) {
    for (let y = 179; y < 190; y++) for (let i = fx - 4; i <= fx + 4; i++) {
      const edge = Math.abs(i - fx) === 4 || y === 179 || y === 189;
      let col = edge ? (fx === 426 ? '#c8a050' : '#5a3a28') : '#e8dcc0';
      if (!edge && Math.hypot(i - fx, y - 183.5) < 2.2) col = face;
      if (!edge && y > 185) col = fx === 438 ? '#4a4a5a' : '#3a2a2a';
      p.set(i, y, col);
    }
  }
  // tier two: pan de muerto, sugar skulls, candles
  for (const bx of [401, 459]) for (let y = 197; y < 204; y++) for (let i = bx - 5; i <= bx + 5; i++) {
    const dx = (i - bx) / 5.5, dy = (y - 203.5) / 6.5;
    if (dx * dx + dy * dy > 1) continue;
    p.set(i, y, Math.abs(i - bx) < 1 || y === 200 ? '#b8621e' : dpick(['#ffd894', '#f0a650', '#c06a28'], 0.3 + dx * 0.3 + dy * 0.2, i, y, 0.6));
  }
  for (const [sx, k] of [[418, 0], [430, 3], [442, 1]]) paintRows(p, SKULL_ROWS, { s: SK_COLS[k][0], E: '#ffd23a', n: SK_COLS[k][1], t: SK_COLS[k][1] }, sx - 3, 197);
  // tier one: tall veladoras, marigold vases, oranges, a glass of water, the copal burner; then every tier's candles
  for (const c of CANDLES) if (c.x >= OF.x0) candle(p, c.x, c.base, c.h, !!c.glass);
  for (const vx of [404, 456]) {
    for (let y = 212; y < 218; y++) for (let i = vx - 2; i <= vx + 2; i++) p.set(i, y, i < vx ? '#e8905a' : '#a85a32');
    for (let k = 0; k < 9; k++) { const fx = vx - 3 + Math.floor(r() * 6), fy = 204 + Math.floor(r() * 8); p.set(fx, fy, CEMP[k % 3]); p.set(fx + 1, fy, CEMP[(k + 1) % 3]); p.set(fx, fy + 1, CEMP[2]); }
    for (let y = 208; y < 212; y++) p.set(vx, y, '#3a7a3a');
  }
  for (const [ox, oy] of [[414, 215], [417, 214], [416, 216], [419, 216]]) for (const [dx, dy] of [[0, 0], [1, 0], [0, 1], [1, 1]]) p.set(ox + dx, oy + dy, dx + dy ? '#e87a10' : '#ffb040');
  for (let y = 211; y < 218; y++) { p.set(446, y, '#c8d8f0'); p.set(447, y, y < 213 ? '#e8f0ff' : '#6a8ab8'); p.set(448, y, '#8aa0c8'); }
  for (let y = 213; y < 218; y++) for (let i = COPAL.x - 3; i <= COPAL.x + 3; i++) p.set(i, y, y === 213 ? '#8a5a3a' : Math.abs(i - COPAL.x) > 3 - (y - 213) / 2 ? '#5a3422' : '#b87448');
  // the marigold arch, cempasúchil with beads of magenta terciopelo
  for (let a = 0; a <= 1; a += 0.004) {
    const ang = Math.PI * (1 - a), R = 43, ax = cx + Math.cos(ang) * R, ay = 196 - Math.sin(ang) * R * 0.98;
    for (let t = -2; t <= 1; t++) {
      const x = Math.round(ax + Math.cos(ang) * t), y = Math.round(ay - Math.sin(ang) * t), k = Math.floor(a * 97);
      p.set(x, y, k % 9 === 4 ? TERC[t & 1] : CEMP[(x + y + (t & 1)) % 4]);
    }
  }
  for (const sx of [cx - 43, cx + 43]) for (let y = 196; y < H; y++) for (let t = -2; t <= 1; t++)
    p.set(sx + t, y, (y + t) % 11 === 5 ? TERC[0] : CEMP[(sx + t + y) % 4]);
}

/* ================= The xolo: a hairless Xoloitzcuintle asleep on the sarape, head on its paws, bat ears up ================= */
const XOLO = { x: 152, y: 216 };
const XC = { o: '#161216', d: '#2e2630', b: '#4e4250', l: '#98807e', L: '#b89488', h: '#5a4c58', e: '#2a2028', i: '#c08a7c', k: '#0a080a', n: '#120c10', p: '#443844' };
const XOLO_ROWS = [                                   // the right ear is drawn per frame (it twitches)
  '....................e.........',
  '...................eie........',
  '...................eie........',
  '..................eiie........',
  '....llllllllll....LLLLLLL.....',
  '..llbbbbbbbbbbll.hhhhhhhhh....',
  '.lbbbbbbbbbbbbbblhhkkhhhhhhh..',
  'lbbbbbbbbbbbbbbbbhhhhhhhhhhhn.',
  'bbbbbbbbbbbbbbbbbbhhhhhhhhhh..',
  'bbbbbbbbbbbbbbbbbbbppppppppp..',
  'dbbbbbbbbbbbbbbbbbbpppppppppp.',
  '.dddddddddddddddddddddddddddd.',
];
function renderXolo(inhale) {                          // breathing out, the back sinks by a row
  const rows = inhale ? XOLO_ROWS : XOLO_ROWS.map((row, y) => y === 4 ? row.slice(0, 18).replace(/l/g, '.') + row.slice(18)
    : y === 5 ? row.slice(0, 17).replace(/b/g, 'l') + row.slice(17) : row);
  return sprite(rows, XC);
}

/* ================= Papel picado: cut tissue-paper flags on strings across the top ================= */
const PP_COLS = ['#ff2e88', '#ff8a1e', '#8338ec', '#9be22a', '#1ec8d2', '#ffd21e', '#e8283c'];
const PP_CUTS = [                                     // cut-outs: a skull, a flower, a lattice (1 = cut away)
  ['00000000000', '00011111000', '00111111100', '00100100100', '00111011100', '00011111000', '00010101000', '00000000000', '01010001010', '00000000000'],
  ['00000000000', '00001010000', '00011111000', '00110101100', '00011111000', '00001010000', '00000100000', '00001110000', '01010001010', '00000000000'],
  ['00000000000', '01010101010', '00101010100', '01010101010', '00101010100', '01010101010', '00101010100', '01010101010', '00000000000', '00000000000'],
];
function makeFlag(col, cut, big) {
  const w = big ? 13 : 9, h = big ? 16 : 11, p = painter(w, h), dark = mixHex(col, '#1a0820', 0.45), lit = mixHex(col, '#fff4e0', 0.25);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    if (y === 0) { p.set(i, y, '#e8d8c0'); continue; }                                     // folded over the string
    const cy = Math.floor((y - 1) * 10 / (h - 3)), cxx = Math.floor(i * 11 / w);
    if (y < h - 2 && cy < 10 && PP_CUTS[cut][cy][cxx] === '1' && i > 0 && i < w - 1) continue;
    if (y >= h - 2 && (i + (y === h - 1 ? 1 : 0)) % 2) continue;                           // the fringed, scalloped hem
    p.set(i, y, i === 0 || i === w - 1 ? dark : y > h - 5 ? lit : col);
  }
  return p.done();
}
// strings: a full one across the top, and two that droop into the upper corners
const STRINGS = [
  { x0: -10, x1: 490, y0: 1, y1: 1, sag: 5, gap: 15, big: true, seed: 1 },
  { x0: -10, x1: 176, y0: 22, y1: 3, sag: 12, gap: 12, big: false, seed: 2 },
  { x0: 372, x1: 490, y0: 3, y1: 20, sag: 9, gap: 12, big: false, seed: 3 },
];
const FLAGS = [];
for (const S of STRINGS) {
  const r = rng(700 + S.seed);
  for (let x = S.x0 + 4; x < S.x1 - 2; x += S.gap) {
    const u = (x - S.x0) / (S.x1 - S.x0);
    FLAGS.push({ x, y: Math.round(lerp(S.y0, S.y1, u) + S.sag * Math.sin(Math.PI * u)), big: S.big, col: Math.floor(r() * 7), cut: Math.floor(r() * 3),
      k: 29 + Math.floor(r() * 23), ph: r(), kf: 11 + Math.floor(r() * 13), pf: r(), s: S.seed });
  }
}
const cordY = (S, x) => { const u = (x - S.x0) / (S.x1 - S.x0); return lerp(S.y0, S.y1, u) + S.sag * Math.sin(Math.PI * u); };

/* ================= Cempasúchil petals, three depths ================= */
const PETAL_COLS = [
  { f: '#ffb300', b: '#c86a00', h: '#ffe070' },
  { f: '#ff8a00', b: '#b85200', h: '#ffc060' },
  { f: '#ffd23a', b: '#d89a10', h: '#fff2a0' },
];
const PETAL_SHAPES = [
  ['xx'],
  ['xx.', 'xxx', '.x.'],
  ['.x.x.', 'xxxxx', 'xxxxx', '.xxx.', '..x..'],
];
function makePetal(size, pc, back) {
  const rows = PETAL_SHAPES[size], w = rows[0].length, h = rows.length, p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < w; i++) if (row[i] === 'x') {
    let col = back ? pc.b : pc.f;
    if (!back && size > 0 && y === (size === 2 ? 1 : 0) && i < 3) col = pc.h;
    if (size === 2 && y === 4) col = pc.b;
    p.set(i, y, col);
  } });
  return p.done();
}
const LANDED = [[112, 0], [131, 1], [204, 2], [247, 0], [306, 1], [346, 2], [361, 0]];   // petals lying on the ledge
function renderVignette() {
  const [c, x] = makeCanvas(W, H); const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.8, 1.5, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 8; d[o + 1] = 2; d[o + 2] = 18; d[o + 3] = Math.round(q * 0.4 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Timings (loop seconds) and tables ================= */
const TROLLEYS = [{ a: 8, b: 32, dir: 1 }, { a: 66, b: 90, dir: -1 }, { a: 126, b: 150, dir: 1 }, { a: 188, b: 212, dir: -1 }];
const TROLLEY_X = [262, 488];                         // out from behind Bellas Artes' plaza, off past the right edge
const TOLLS = [44, 48, 52, 164, 168, 172];             // the church bell tolls for the dead
const ORGAN_T = [[26, 36], [116, 126], [202, 212]];    // the organillero cranks his barrel organ
const BURSTS = [{ t: 150.2, x: 292, y: 104, col: '#ff5aa8' }, { t: 151.6, x: 306, y: 94, col: '#ffc23a' }, { t: 153.3, x: 284, y: 114, col: '#a87aff' }];
const PLANES = [{ a: 18, b: 64 }, { a: 138, b: 184 }]; // landing lights descending toward the airport, east
const EAR_FLICKS = [21.4, 58.2, 97.5, 133.1, 177.8, 214.6];
const TAIL_WAGS = [40, 112, 158, 226];
const NEON_FLICK = [[33.2, 34.8], [141.5, 142.3], [196, 197.5]];
const WALKERS = [];
{
  const r = rng(515);
  for (let i = 0; i < 15; i++) {
    const dir = r() < 0.5 ? 1 : -1, speed = 5 + r() * 5;
    WALKERS.push({ id: i, dir, n: Math.max(1, Math.round(speed * LOOP / 370)), ph: r(), kind: Math.floor(r() * WALKER_KINDS.length), row: r() < 0.5 ? 186 : 187, kf: 180 + Math.floor(r() * 80) });
  }
}
// traffic: each lane is a train of evenly spaced slots moving at one speed, so cars never overlap; a slot is empty,
// a taxi, a vocho or a sedan, picked again each time it wraps round out of sight
const LANE_K = [26, 30, 22], LANE_SLOTS = [6, 6, 4], LANE_SPAN = 420;
const AMBU = { lane: 2, slot: 0, cyc: 9 };            // one pass of the near lane's first slot is an ambulance, lights and siren
const AMBU_T = +(AMBU.cyc * LOOP / LANE_K[AMBU.lane]).toFixed(1);   // when it enters on the left (98.2 s)
const PETALS = [[], [], []];
{
  const r = rng(4242);
  [[26, 7, 10, 2, 30], [16, 10, 13, 4, 55], [8, 13, 16, 8, 90]].forEach(([count, nMin, nMax, amp, drift], size) => {
    for (let i = 0; i < count; i++) PETALS[size].push({
      id: size * 100 + i, size, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(),
      ks: 50 + Math.floor(r() * 40), p2: r(), kr: 20 + Math.floor(r() * 30), p3: r(),
      kf: 30 + Math.floor(r() * 40), p4: r(), amp: amp * (0.7 + r() * 0.6), drift: drift * (0.6 + r() * 0.8), c: Math.floor(r() * 3),
    });
  });
}
const STEAM = [];
{
  const r = rng(61);
  for (let k = 0; k < 4; k++) STEAM.push({ x: CHOCO.x, y: CHOCO.y - 1, n: 40 + Math.floor(r() * 12), ph: r(), k: 20 + Math.floor(r() * 20), p: r(), id: k, h: 16, a: 0.8, fg: true });
  for (let k = 0; k < 3; k++) STEAM.push({ x: ELOTE.x - 2, y: 176, n: 44 + Math.floor(r() * 10), ph: r(), k: 18 + Math.floor(r() * 20), p: r(), id: 10 + k, h: 10, a: 0.5 });
  for (let k = 0; k < 3; k++) STEAM.push({ x: TAMAL.x + 3, y: 172, n: 44 + Math.floor(r() * 10), ph: r(), k: 18 + Math.floor(r() * 20), p: r(), id: 20 + k, h: 11, a: 0.5 });
  for (let k = 0; k < 5; k++) STEAM.push({ x: COPAL.x, y: COPAL.y - 1, n: 30 + Math.floor(r() * 8), ph: r(), k: 14 + Math.floor(r() * 14), p: r(), id: 30 + k, h: 30, a: 0.55, smoke: true, fg: true });
}
const BALLOONS = [[-3, 0, '#ff3d9a'], [2, -1, '#ffd23a'], [0, -5, '#f4f0ea'], [-4, -5, '#3ad6c8'], [4, -4, '#8338ec'], [1, -9, '#ff8a1e']];
// a picked weather (site/js/engine/weather.js): the city's own cempasúchil petals, and the ones lying on the ledge, show
// only with its own weather. Far drops land on the Alameda and the Centro and catch the floodlit palace, the tower's
// crown and the church; mid ones land on Juárez under the park lamps and the arches; near ones fall past the terrace
// through the ofrenda's candlelight.
const WEATHER = weatherPlans(['marigold']);
const WX_LAND = [[160, 186], [ST.y0 + 2, ST.y1], [H + 4, H + 4]];
const WX_FAR = [
  { x: BA.cx, y: 136, rx: 66, ry: 46, col: '#ffe2b8' }, { x: TL.cx, y: 38, rx: 12, ry: 16, col: '#d0a8ff' },
  { x: CH.cx + 8, y: 132, rx: 22, ry: 22, col: '#ffb870' },
];
const WX_MID = [
  { x: 70, y: 180, rx: 76, ry: 14, col: '#ffb848' }, ...BA_LAMPS.map(x => ({ x, y: 164, rx: 9, ry: 16, col: '#ffe0a0' })),
  { x: NEON.x + 1, y: 206, rx: 9, ry: 18, col: '#ff6a78' },
];
const WX_NEAR = [
  { x: OF.cx, y: 196, rx: 56, ry: 46, col: '#ffb458' }, { x: LANT.x, y: 214, rx: 20, ry: 16, col: '#ffcc70' },
  { x: 70, y: 216, rx: 44, ry: 18, col: '#ffb060' }, { x: 258, y: 216, rx: 50, ry: 12, col: '#ffc070' },
];
const wxDepths = far => [far, WX_MID, WX_NEAR].map(lights => ({ land: WX_LAND, lights }));
const WX = wxDepths(WX_FAR);
const WX_BB = wxDepths([...WX_FAR, { x: BB.x + (BB.w >> 1), y: BB.y + 16, rx: 32, ry: 18, col: '#ffe0b0' }]);   // + the sign's lamps

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, WISPS, VOLC_C, FAR, TLR, LNR, BA_C, MID, LANE_C, WALK_R, STREET_C, NBR, NEON_C, FG_C, XOLO_SPR, VIG_C;
let CARS, TROLLEY_SPR, AMBU_SPR, WALK_SPR, FLAG_SPR, CORD_C, PETAL_SPR, VENDOR_SPR;
let BB_FR_C, BB_POOL_C, AD_C, PITCH;
function prerender() {
  HL = {
    moon: makeHalo(30, '#aab4ff', 0.2, 1.8), moonCore: makeHalo(12, '#e8ecff', 0.25, 1.5),
    ba: makeHalo(84, '#ffcf8a', 0.14, 1.6), dome: makeHalo(34, '#ffc060', 0.2, 1.5), portal: makeHalo(14, '#ffc878', 0.4, 1.4),
    tl: makeHalo(18, '#b08aff', 0.3, 1.4), red: makeHalo(4, '#ff3030', 0.6, 1), white: makeHalo(4, '#e8f0ff', 0.6, 1),
    lamp: makeHalo(9, '#ffe0a0', 0.5, 1.3), park: makeHalo(18, '#ffc070', 0.22, 1.5), arch: makeHalo(10, '#ffa030', 0.2, 1.5),
    head: makeHalo(5, '#fff0c8', 0.45, 1.3), tail: makeHalo(3, '#ff3030', 0.45, 1), beam: makeHalo(9, '#fff0c8', 0.08, 1.2),
    siren: [makeHalo(10, '#ff2a2a', 0.6, 1.2), makeHalo(10, '#3a6aff', 0.6, 1.2)], spark: makeHalo(6, '#a8d0ff', 0.8, 1),
    neon: makeHalo(14, '#ff3a4a', 0.35, 1.4), bell: makeHalo(12, '#ffb060', 0.25, 1.4),
    candle: makeHalo(10, '#ffcf70', 0.5, 1.4), ofrenda: makeHalo(60, '#ff9a40', 0.14, 1.5), lantern: makeHalo(26, '#ffc060', 0.22, 1.5),
    ledge: makeHalo(34, '#ffa850', 0.1, 1.5), bbLamp: makeHalo(6, '#fff0c0', 0.6, 1.2), bbSky: makeHalo(38, '#e0c8ff', 0.1, 1.5),
    plane: makeHalo(8, '#f0f4ff', 0.55, 1.2), burst: BURSTS.map(b => makeHalo(24, b.col, 0.4, 1.3)), win: makeHalo(3, '#ffd890', 0.3, 1),
  };
  BB_FR_C = renderBillboardFrame(); BB_POOL_C = renderPoolMask(); AD_C = AD_SUBS.map(renderAd);
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  SKY_C = renderSky(); MOON_C = renderMoon();
  WISPS = [makeWisps(7, 30, 20, 1, '#3a2a62', '#2c2052'), makeWisps(19, 78, 22, 2, '#5a3a72', '#44306a')];
  VOLC_C = renderVolcanoes(); FAR = renderFar(); TLR = renderTL(); LNR = renderLN(); BA_C = renderBA();
  MID = renderMid(); LANE_C = renderLane(); WALK_R = renderWalk(); STREET_C = renderStreet();
  NBR = renderNeighbour(); NEON_C = [renderNeonText(false), renderNeonText(true)];
  FG_C = renderForeground(); XOLO_SPR = [renderXolo(false), renderXolo(true)]; VIG_C = renderVignette();
  CARS = [
    vehicle(TAXI_ROWS, vkey(TAXI_PINK, { W: '#fffaf8', w: '#f0ecf0', v: '#bcb4c4', P: TAXI_PINK, Q: mixHex(TAXI_PINK, '#fff6e8', 0.25), L: '#fffaf8', a: '#ffe8a0' })),
    ...CAR_COLS.slice(0, 4).map(c => vehicle(VOCHO_ROWS, vkey(c))),
    ...CAR_COLS.slice(4).map(c => vehicle(SEDAN_ROWS, vkey(c))),
  ];
  AMBU_SPR = vehicle(AMBU_ROWS, vkey('#f4f4f8', { R: '#ffffff', L: '#ffffff', w: '#e8e8f0', v: '#a8a8b8', r: '#e82a2a', x: '#e82a2a', s: '#5a5a66' }));
  TROLLEY_SPR = vehicle(TROLLEY_ROWS, vkey('#2a5ac8', { b: '#2a5ac8', w: '#e8eef8', l: '#ffe29a', g: '#2a3450', d: '#0e0c14', k: '#1a1a1e' }));
  WALK_SPR = WALKER_KINDS.map(([kind, a, b]) => walker(kind, a, b));
  VENDOR_SPR = {
    elote: sprite(['.hhh.', '.fff.', '.fff.', 'aaaaa', '.aaa.', '.aaa.', '.ppp.', '.p.p.'], { h: '#2a1a14', f: '#b8845e', a: '#f0ece0', p: '#2a2432' }),
    tamal: sprite(['.hhh.', '.fff.', '.jjj.', 'jjjjj', '.jjj.', '.p.p.'], { h: '#e8e0d0', f: '#a87450', j: '#4a6a3a', p: '#2a2432' }),
    globo: sprite(['.hhh.', '.fff.', '.fff.', '.jjj.', 'jjjjj', '.jjj.', '.ppp.', '.p.p.'], { h: '#3a2a20', f: '#c0906a', j: '#c83a4a', p: '#2a2432' }),
    organ: sprite(['.kkk.', 'kkkkk', '.fff.', '.fff.', '.uuu.', 'uuuuu', '.uuu.', '.uuu.', '.u.u.'], { k: '#5a4a2e', f: '#b8845e', u: '#9a8458' }),
  };
  FLAG_SPR = PP_COLS.map(col => [0, 1, 2].map(cut => [makeFlag(col, cut, true), makeFlag(col, cut, false)]));
  { const p = painter();
    for (const S of STRINGS) for (let x = Math.max(0, S.x0); x < Math.min(W, S.x1); x++) p.set(x, Math.round(cordY(S, x)), '#cbbba4');
    CORD_C = p.done(); }
  PETAL_SPR = PETAL_COLS.map(pc => [0, 1, 2].map(s => [makePetal(s, pc, false), makePetal(s, pc, true)]));
}

/* ================= Per-frame drawing ================= */
function drawStars(t) {
  ctx.fillStyle = '#e6e2ff';
  for (const s of STARS) {
    const a = s.b * (0.5 + 0.5 * wave(t, s.k, s.ph));
    ctx.globalAlpha = a; ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha = a * 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
}
function drawMoon() {
  halo(HL.moon, MOON.x, MOON.y, 0.9); halo(HL.moonCore, MOON.x - 1, MOON.y, 0.8);
  ctx.drawImage(MOON_C, MOON.x - 8, MOON.y - 8);
}
function drawWisps(t) {
  ctx.globalAlpha = 0.75;
  for (const L of WISPS) { const off = scroll(t, L.k, W); ctx.drawImage(L.c, off - W, L.y); ctx.drawImage(L.c, off, L.y); }
  ctx.globalAlpha = 1;
}
function drawPlume(t) {                               // Popocatépetl's thin fumarole, drifting off east
  ctx.fillStyle = '#9a94b8';
  for (let k = 0; k < 9; k++) {
    const u = frac(t * 5 / LOOP + k / 9);
    const x = POPO.x + u * 16 + Math.sin(u * 5 + k) * 1.2, y = POPO.y - 2 - u * 17 - u * u * 4;
    ctx.globalAlpha = 0.34 * Math.sin(Math.PI * Math.min(1, u * 1.4 + 0.05)) * (1 - u * 0.5);
    ctx.fillRect(Math.round(x), Math.round(y), u > 0.35 ? 2 : 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawPlanes(t) {                              // landing lights sliding down toward the airport, east of the Centro
  for (const pl of PLANES) {
    if (t < pl.a || t >= pl.b) continue;
    const s = (t - pl.a) / (pl.b - pl.a), x = Math.round(lerp(478, 300, s)), y = Math.round(lerp(64, 146, s ** 1.15));
    const f = smooth(0, 0.08, s) * (1 - smooth(0.9, 1, s));
    halo(HL.plane, x, y, 0.7 * f);
    ctx.globalAlpha = f; ctx.fillStyle = '#ffffff'; ctx.fillRect(x, y, 1, 1);
    ctx.fillStyle = '#ff4040'; ctx.fillRect(x + 2, y - 1, 1, 1);
    if (frac(t / 1.2) < 0.1) { ctx.fillStyle = '#ffffff'; ctx.fillRect(x - 2, y - 1, 1, 1); }
    ctx.globalAlpha = 1;
  }
}
function drawWins(list, t) {
  for (const w of list)
    if (hash(w.id + list.length * 7, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) { ctx.fillStyle = w.col; ctx.fillRect(w.x, w.y, w.w || 1, 1); }
}
function drawFarFx(t) {
  drawWins(FAR.wins, t);
  ctx.globalAlpha = 1;
  for (const l of FAR.twink) {
    ctx.globalAlpha = 0.35 + 0.65 * (0.5 + 0.5 * wave(t, l.n, l.ph)); ctx.fillStyle = l.col; ctx.fillRect(l.x, l.y, 1, 1);
  }
  ctx.globalAlpha = 1;
  for (const b of FAR.beacons) if (frac(t / 2 + b.ph) < 0.4) { ctx.fillStyle = '#ff3a3a'; ctx.fillRect(b.x, b.y, 1, 1); halo(HL.red, b.x, b.y, 0.6); }
}
function drawFireworks(t) {                           // cohetes over the Zócalo, far down Madero
  for (let k = 0; k < BURSTS.length; k++) {
    const b = BURSTS[k], age = t - b.t;
    if (age > -1 && age < 0) {                         // the rocket's climb, a short bright streak
      const y = Math.round(b.y + 44 * -age);
      ctx.fillStyle = '#fff0c8'; ctx.globalAlpha = 0.9; ctx.fillRect(b.x, y, 1, 1); ctx.globalAlpha = 0.4; ctx.fillRect(b.x, y + 1, 1, 2);
    } else if (age >= 0 && age < 2.6) {
      const f = 1 - age / 2.6, rr = 4 + 14 * (1 - Math.exp(-age * 2.4)), drop = age * age * 2.4;
      halo(HL.burst[k], b.x, b.y + Math.round(drop * 0.5), 0.9 * f);
      ctx.fillStyle = age < 0.35 ? '#fff6e0' : b.col;
      for (let j = 0; j < 22; j++) {
        const a = TAU * j / 22 + k * 0.7, tw = hash(j, k, Math.floor(age * 14)) < 0.85 ? 1 : 0.35;
        for (let q = 0; q < 2; q++) {                  // each spark and the fading tail behind it
          const rq = rr - q * 2.5;
          ctx.globalAlpha = f * tw * (q ? 0.45 : 1);
          ctx.fillRect(Math.round(b.x + Math.cos(a) * rq), Math.round(b.y + Math.sin(a) * rq * 0.9 + drop), 1, 1);
        }
      }
      if (age < 0.25) { ctx.globalAlpha = 1 - age * 4; ctx.fillStyle = '#ffffff'; ctx.fillRect(b.x - 1, b.y - 1, 3, 3); }
    }
  }
  ctx.globalAlpha = 1;
}
const CROWN_BASE = ['#8338ec', '#ff3d9a', '#ff8a1e', '#ffd21e', '#ff3d9a'];
const CROWN_RAMP = [];
for (let i = 0; i < 60; i++) { const f = i / 60 * CROWN_BASE.length, k = Math.floor(f); CROWN_RAMP.push(mixHex(CROWN_BASE[k], CROWN_BASE[(k + 1) % CROWN_BASE.length], f - k)); }
function drawTL(t) {
  ctx.drawImage(TLR.c, 0, 0);
  drawWins(TLR.wins, t);
  TLR.crown.forEach(([y0, , hw], k) => {              // the crown's LED edges cycle through the Día de Muertos colours
    ctx.fillStyle = CROWN_RAMP[Math.floor(frac(4 * t / LOOP + k * 0.07) * CROWN_RAMP.length) % CROWN_RAMP.length];
    ctx.fillRect(TL.cx - hw, y0, 2 * hw + 1, 1);
    ctx.globalAlpha = 0.6; ctx.fillRect(TL.cx - hw, y0 + 1, 1, 3); ctx.fillRect(TL.cx + hw, y0 + 1, 1, 3); ctx.globalAlpha = 1;
  });
  halo(HL.tl, TL.cx, TL.top - 10, 0.75 + 0.2 * wave(t, 8));
  if (frac(t / 1.5) < 0.5) { ctx.fillStyle = '#ff3030'; ctx.fillRect(TL.cx - 1, TL.tip, 1, 1); halo(HL.red, TL.cx - 1, TL.tip, 0.9); }
}
function drawLN(t) { ctx.drawImage(LNR.c, 0, 0); drawWins(LNR.wins, t); }
function drawLane(t) {                                // Eje Central: car lights and the trolleybuses on their wires
  ctx.drawImage(LANE_C, 0, 0);
  for (let k = 0; k < 9; k++) {
    const dir = k & 1 ? 1 : -1, u = frac(t * (7 + k % 4) / LOOP + k * 0.137), x = Math.round(dir > 0 ? 270 + u * 220 : 490 - u * 220);
    if (x < 281 || x >= W) continue;
    ctx.fillStyle = dir > 0 ? '#ff5a4a' : '#fff4d0'; ctx.fillRect(x, 178 - (k & 1), 2, 1);
  }
  for (const tr of TROLLEYS) {
    if (t < tr.a || t >= tr.b) continue;
    const s = (t - tr.a) / (tr.b - tr.a), spr = TROLLEY_SPR[tr.dir > 0 ? 0 : 1], w = spr.width;
    const x = Math.round(tr.dir > 0 ? lerp(TROLLEY_X[0], TROLLEY_X[1], s) : lerp(TROLLEY_X[1], TROLLEY_X[0], s) - w);
    ctx.drawImage(spr, x, 172);
    const rear = tr.dir > 0 ? x + 5 : x + w - 6;
    ctx.fillStyle = '#2a2a34';
    for (let j = 0; j < 7; j++) { ctx.fillRect(rear - tr.dir * j, 171 - j, 1, 1); ctx.fillRect(rear + 2 - tr.dir * j, 171 - Math.round(j * 5 / 6), 1, 1); }
    const fx = tr.dir > 0 ? x + w - 1 : x;
    halo(HL.head, fx, 176, 0.7);
    if (hash(tr.a, step(t, 960), 7) < 0.05) { ctx.fillStyle = '#e0f0ff'; ctx.fillRect(rear - tr.dir * 6, 164, 1, 2); halo(HL.spark, rear - tr.dir * 6, 164, 0.9); }
  }
}
function drawBA(t) {
  ctx.drawImage(BA_C, 0, 0);
  halo(HL.dome, BA.cx, 110, 0.75 + 0.08 * wave(t, 6));
  halo(HL.portal, BA.cx, 152, 0.8);
  const g = t % 20;                                   // a glint runs down one of the dome's gold ribs
  if (g < 1.2) { const j = Math.floor(g / 1.2 * 12); ctx.fillStyle = '#fffbe0'; ctx.fillRect(BA.cx - 9 + Math.floor(j / 4), 106 + j, 1, 1); }
  for (const lx of BA_LAMPS) {
    ctx.fillStyle = '#fff4d0'; ctx.fillRect(lx - 1, 160, 3, 1); ctx.fillStyle = '#ffffff'; ctx.fillRect(lx, 159, 1, 1);
    halo(HL.lamp, lx, 160, 0.85 + 0.1 * wave(t, 900 + lx, 0.2));
  }
}
function bellSwing(t) {
  for (const g of [TOLLS[0], TOLLS[3]]) if (t > g - 3 && t < g + 12) return env(t, g - 3, g + 12, 2.6) * Math.cos(Math.PI * (t - g) / 4);
  return 0;
}
function drawMid(t) {
  ctx.drawImage(MID.c, 0, 0);
  drawWins(MID.wins, t);
  for (const l of MID.lamps) halo(HL.park, l.x, l.y, 0.9);
  const a = bellSwing(t), bx = BELL.x + Math.round(a * 1.4), lift = Math.abs(a) > 0.6 ? 1 : 0;
  halo(HL.bell, BELL.x, 121, 0.55 + 0.35 * Math.abs(a));
  ctx.fillStyle = '#d8a050'; ctx.fillRect(bx - 1, 117 - lift, 3, 1); ctx.fillRect(bx - 1, 118 - lift, 3, 2);
  ctx.fillStyle = '#9a6a30'; ctx.fillRect(bx - 2 + (a > 0.3 ? 1 : 0), 120 - lift, 5 - (Math.abs(a) > 0.3 ? 1 : 0), 1);
  ctx.fillStyle = '#6a4a2a'; ctx.fillRect(BELL.x, 116, 1, 1);
}
function drawOwnAd(t) {
  const q = t * 30 / LOOP + 0.25, beat = frac(q) < 0.04;                         // the line changes every 8 s
  ctx.drawImage(AD_C[beat ? 2 : Math.floor(q) & 1], BB.x, BB.y);
  ctx.fillStyle = '#fff6ea';                                                   // steam off the warm loaf
  for (let k = 0; k < 3; k++) {
    const u = frac(t * (36 + k * 7) / LOOP + k * 0.33), y = BB.y + BUN.y + 1 - Math.floor(u * 7);
    const x = BB.x + BUN.x + 5 + k * 3 + Math.round(Math.sin(u * 5 + k * 2 + TAU * 9 * t / LOOP) * 1.2);
    ctx.globalAlpha = 0.7 * Math.sin(Math.PI * u); ctx.fillRect(x, y, 1, 1);
  }
  const g = t % 10;                                                            // a sugar sparkle
  if (g < 0.5) { ctx.globalAlpha = 1 - g * 2; ctx.fillRect(BB.x + BUN.x + 5 + Math.floor(hash(Math.floor(t / 10)) * 7), BB.y + BUN.y + 5, 1, 1); }
  ctx.globalAlpha = 1;
}
function drawBillboard(t, pitch) {
  halo(HL.bbSky, BB.x + (BB.w >> 1), BB.y + 10, 0.9);
  ctx.drawImage(BB_FR_C, BB_FR.x, BB_FR.y);
  if (!PITCH.covers(t, pitch)) drawOwnAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  const tired = inEvent(t, BB_TIRED) ? (hash(11, Math.floor(t * 24)) < 0.55 ? 0.15 : 0.7) : 1;
  if (tired < 1) { ctx.globalAlpha = (1 - tired) * 0.5; ctx.drawImage(BB_POOL_C, BB.x, BB.y); ctx.globalAlpha = 1; }
  BB_LAMPS.forEach((lx, k) => {
    const I = (k === 1 ? tired : 1) * (0.92 + 0.08 * wave(t, 1100 + k * 131, k * 0.3)), ly = BB.y + BB.h + 2;
    ctx.fillStyle = I > 0.5 ? '#fff6dc' : '#8a7a70'; ctx.fillRect(lx - 1, ly, 3, 1);
    halo(HL.bbLamp, lx, ly, I);
  });
}
function drawSteam(t, fg) {                          // fg: the terrace's steam and copal smoke, else the vendors' pots
  for (const s of STEAM) {
    if (!!s.fg !== fg) continue;
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q);
    const x = Math.round(s.x + wave(t, s.k, s.p) * 1.8 * u + Math.sin(u * 7 + s.id) * 1.5 * u + (s.smoke ? u * 5 : 0));
    const y = Math.round(s.y - u * s.h), a = s.a * Math.min(1, u * 6) * (1 - u) * (1 - u * 0.3);
    ctx.fillStyle = s.smoke ? '#d8d0dc' : '#fffaf4';
    ctx.globalAlpha = a; ctx.fillRect(x, y, 1, 2);
    if (u > 0.25) { ctx.globalAlpha = a * 0.55; ctx.fillRect(x + (s.id & 1 ? 1 : -1), y - 1, 1, 2); }
  }
  ctx.globalAlpha = 1;
}
function drawWalk(t) {
  ctx.drawImage(WALK_R.c, 0, 0);
  for (let k = 0; k < WALK_R.glow.length; k += 3) {     // fairy lights woven through the arches
    const gp = WALK_R.glow[k], on = hash(k, step(t, 480), 5) < 0.8;
    if (on) { ctx.fillStyle = '#fff4c0'; ctx.fillRect(gp.x, gp.y - 1, 1, 1); }
    if (k % 6 === 0) halo(HL.arch, gp.x, gp.y, 0.75 + 0.2 * wave(t, 300 + k, 0.1));
  }
  drawSteam(t, false);
  ctx.drawImage(VENDOR_SPR.elote, ELOTE.x + 6, 179);
  ctx.drawImage(VENDOR_SPR.tamal, TAMAL.x - 9, 178);
  ctx.drawImage(VENDOR_SPR.globo, GLOBO.x + 1, 179);
  BALLOONS.forEach(([dx, dy, col], k) => {            // the balloon bunch bobbing on its pole
    const x = GLOBO.x + dx + Math.round(wave(t, 31 + k * 3, k * 0.2) * 0.8), y = 166 + dy + Math.round(wave(t, 43 + k * 5, k * 0.37) * 0.7);
    ctx.fillStyle = '#6a6a70'; ctx.fillRect(x, y + 2, 1, 1);
    ctx.fillStyle = col; ctx.fillRect(x - 1, y - 1, 3, 3);
    ctx.fillStyle = '#ffffff'; ctx.globalAlpha = 0.6; ctx.fillRect(x - 1, y - 1, 1, 1); ctx.globalAlpha = 1;
    if (col === '#f4f0ea') { ctx.fillStyle = '#1a1418'; ctx.fillRect(x - 1, y, 1, 1); ctx.fillRect(x + 1, y, 1, 1); }   // a calavera balloon
  });
  ctx.drawImage(VENDOR_SPR.organ, ORGAN.x - 9, 178);  // the organillero, his crank turning while he plays
  const playing = ORGAN_T.some(([a, b]) => t >= a && t < b);
  const ang = playing ? TAU * t * 1.5 : 0, hx = ORGAN.x + 4 + Math.round(Math.cos(ang) * 1.4), hy = 178 + Math.round(Math.sin(ang) * 1.4);
  ctx.fillStyle = '#c8b890'; ctx.fillRect(ORGAN.x + 4, 178, 1, 1); ctx.fillStyle = '#e8d8b0'; ctx.fillRect(hx, hy, 1, 1);
  if (playing && frac(t * 2) < 0.5) { ctx.fillStyle = '#b8845e'; ctx.fillRect(ORGAN.x - 5, 182, 2, 1); }   // his arm
  for (const wk of WALKERS) {
    const { u } = cycle(t, wk.n, wk.ph), x = Math.round(wk.dir > 0 ? lerp(-8, 352, u) : lerp(352, -8, u));
    const fade = 1 - smooth(328, 350, x);
    if (fade <= 0.02) continue;
    const fr = WALK_SPR[wk.kind][Math.floor(t * wk.kf / LOOP * 8) & 1], h = fr.height;
    ctx.globalAlpha = fade; ctx.drawImage(fr, x - 2, wk.row - h + 1); ctx.globalAlpha = 1;
  }
}
const LIGHT_AT = {};                                  // where a vehicle sprite's head- and taillights sit
function lightsOf(rows) {
  if (LIGHT_AT[rows[0] + rows.length]) return LIGHT_AT[rows[0] + rows.length];
  let h = null, tl = null;
  rows.forEach((row, y) => { const a = row.indexOf('h'), b = row.indexOf('T'); if (a >= 0) h = [a, y]; if (b >= 0) tl = [b, y]; });
  return (LIGHT_AT[rows[0] + rows.length] = { h, tl, w: Math.max(...rows.map(r => r.length)), rows: rows.length });
}
const CAR_ROWS = [TAXI_ROWS, VOCHO_ROWS, VOCHO_ROWS, VOCHO_ROWS, VOCHO_ROWS, SEDAN_ROWS, SEDAN_ROWS, SEDAN_ROWS, SEDAN_ROWS];
function drawVehicle(spr, rows, x, laneY, dir, t, siren) {
  const L = lightsOf(rows), w = L.w, y = laneY - L.rows + 1;
  const hx = dir > 0 ? x + L.h[0] : x + w - 1 - L.h[0], tx = dir > 0 ? x + L.tl[0] : x + w - 1 - L.tl[0];
  halo(HL.beam, hx + dir * 10, laneY, 0.8);
  ctx.drawImage(spr[dir > 0 ? 0 : 1], x, y);
  halo(HL.head, hx, y + L.h[1], 0.7); halo(HL.tail, tx, y + L.tl[1], 0.8);
  if (siren) {
    const ph = Math.floor(t * 6) & 1, sx = dir > 0 ? x + 4 : x + w - 10;
    ctx.fillStyle = ph ? '#ff2a2a' : '#3a6aff'; ctx.fillRect(sx + (ph ? 0 : 3), y, 3, 1);
    halo(HL.siren[ph], sx + 2, y, 0.9);
  }
}
function drawTraffic(t) {
  LANES.forEach((ln, li) => {
    const K = LANE_K[li], N = LANE_SLOTS[li];
    for (let s = 0; s < N; s++) {
      const q = K * t / LOOP + s / N, cyc = Math.floor(q), u = q - cyc;
      const x = Math.round(ln.dir > 0 ? -40 + u * LANE_SPAN : 380 - u * LANE_SPAN);
      if (x < -30 || x > NB.x0 + 2) continue;
      if (li === AMBU.lane && s === AMBU.slot && ((cyc % K) + K) % K === AMBU.cyc) { drawVehicle(AMBU_SPR, AMBU_ROWS, x, ln.y, ln.dir, t, true); continue; }
      const h = hash(li * 10 + s, cyc, 21);
      if (h < 0.22) continue;
      const k = h < 0.52 ? 0 : 1 + Math.floor(hash(li * 10 + s, cyc, 22) * (CARS.length - 1));
      drawVehicle(CARS[k], CAR_ROWS[k], x, ln.y, ln.dir, t, false);
    }
  });
}
function drawNeighbour(t) {
  ctx.drawImage(NBR.c, 0, 0);
  for (const w of NBR.wins) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 9) < w.p) {
    ctx.globalAlpha = 0.8; ctx.fillStyle = w.col; ctx.fillRect(w.x, w.y, 4, 5); ctx.globalAlpha = 1;
  }
  const on = inEvent(t, NEON_FLICK) ? hash(5, Math.floor(t * 18)) < 0.5 : true;
  ctx.drawImage(NEON_C[on ? 1 : 0], NEON.x, NEON.y);
  if (on) halo(HL.neon, NEON.x + 1, NEON.y + 14, 0.8);
}
const flicker = (t, k) => 0.72 + 0.14 * wave(t, 1733 + k * 97, 0.2 + k * 0.13) + 0.09 * wave(t, 2711 + k * 53, 0.6) + 0.05 * wave(t, 419 + k * 11);
function drawFlame(x, y, f, k, t) {
  const fx = x + (wave(t, 997 + k * 31, 0.4 + k * 0.1) > 0.65 ? 1 : 0);
  px(ctx, x, y + 1, '#fff4c0'); px(ctx, fx, y, '#ffd060');
  if (f > 0.74) px(ctx, fx, y - 1, '#ff9a30');
  halo(HL.candle, x, y, f);
}
function drawXolo(t) {
  const X = XOLO.x, Y = XOLO.y;
  ctx.drawImage(XOLO_SPR[wave(t, 60, 0.1) > 0.15 ? 1 : 0], X, Y);
  const f = EAR_FLICKS.some(e => t >= e && t < e + 0.35) ? 1 : 0;              // the right ear twitches
  ctx.fillStyle = XC.e;                                                          // the ear: dark rim, pink lit inside
  ctx.fillRect(X + 25 + f, Y, 1, 1); ctx.fillRect(X + 24 + f, Y + 1, 1, 1); ctx.fillRect(X + 26 + f, Y + 1, 1, 1);
  ctx.fillRect(X + 24, Y + 2, 1, 1); ctx.fillRect(X + 26, Y + 2, 1, 1); ctx.fillRect(X + 23, Y + 3, 1, 1); ctx.fillRect(X + 26, Y + 3, 1, 1);
  ctx.fillStyle = XC.i; ctx.fillRect(X + 25 + f, Y + 1, 1, 1); ctx.fillRect(X + 25, Y + 2, 1, 1); ctx.fillRect(X + 24, Y + 3, 2, 1);
  let wag = 0;
  for (const w of TAIL_WAGS) { const d = t - w; if (d >= 0 && d < 1.6) wag = Math.round(Math.sin(d * TAU * 2) * 1.5 * (1 - d / 1.6)); }
  ctx.fillStyle = XC.d; ctx.fillRect(X - 1, Y + 9, 1, 1); ctx.fillRect(X - 2, Y + 8 + wag, 1, 1); ctx.fillRect(X - 3, Y + 7 + wag * 2, 1, 1);
  ctx.fillStyle = XC.l; ctx.fillRect(X - 4, Y + 6 + wag * 2, 1, 1);
}
function drawForeground(t, petals) {
  ctx.drawImage(FG_C, 0, 0);
  if (petals) for (const [fx, k] of LANDED) { const pc = PETAL_COLS[k]; rect(ctx, fx, LEDGE - 1, 2, 1, pc.f); px(ctx, fx + 2, LEDGE - 1, pc.b); px(ctx, fx + 1, LEDGE - 2, pc.h); }
  const fo = flicker(t, 0);
  halo(HL.ofrenda, OF.cx, 200, fo); halo(HL.ledge, 70, 220, 0.8); halo(HL.ledge, 258, 222, 0.7);
  CANDLES.forEach((c, k) => drawFlame(c.x, c.base - c.h - 1, flicker(t, k + 1), k, t));
  const fl = flicker(t, 20);
  px(ctx, LANT.x, 217, '#fff4c0'); px(ctx, LANT.x, 216, fl > 0.74 ? '#ffd060' : '#ffb040');
  halo(HL.lantern, LANT.x, 216, fl);
  drawSteam(t, true);
  drawXolo(t);
}
function drawPapel(t) {
  ctx.drawImage(CORD_C, 0, 0);
  const gust = 0.6 + 0.4 * (0.6 * wave(t, 5, 0.1) + 0.4 * wave(t, 13, 0.7));
  for (const f of FLAGS) {
    const spr = FLAG_SPR[f.col][f.cut][f.big ? 0 : 1], w = spr.width;
    const sway = (f.big ? 0.28 : 0.34) * gust * Math.sin(TAU * (f.k * t / LOOP + f.ph) - f.x * 0.035);
    const sy = 0.72 + 0.28 * Math.abs(Math.cos(TAU * (f.kf * t / LOOP + f.pf)));
    ctx.setTransform(1, 0, sway, sy, f.x, f.y);
    ctx.drawImage(spr, -(w >> 1), 0);
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}
function drawPetal(b, t) {
  const q = t * b.n / LOOP + b.ph, cyc = Math.floor(q) % b.n, u = q - Math.floor(q);
  const sh = PETAL_SHAPES[b.size], w = sh[0].length, h = sh.length, sw = wave(t, b.ks, b.p2);
  const y = -h * 2 - 8 + u * (H + h * 4 + 16) - Math.abs(sw) * (b.size + 1) * 1.5;
  const x = -40 - b.drift * 0.5 + hash(b.id, cyc, 5) * (W + 60) + u * b.drift + sw * b.amp;
  const fl = Math.cos(TAU * (b.kf * t / LOOP + b.p4)), spr = PETAL_SPR[(b.c + cyc) % 3][b.size][fl < 0 ? 1 : 0];
  if (b.size === 0) { ctx.drawImage(spr, Math.round(x), Math.round(y)); return; }
  ctx.setTransform(1, 0, 0, 1, Math.round(x), Math.round(y));
  ctx.rotate(sw * 0.7 + 0.35 * wave(t, b.kr, b.p3)); ctx.scale(1, Math.max(0.25, Math.abs(fl)));
  ctx.drawImage(spr, -(w >> 1), -(h >> 1));
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

/* ================= Frame compositor (back to front) ================= */
// billboard: false leaves the sign out (the loop videos); weather: a WEATHERS pick (site/js/engine/weather.js), the
// city's own when left out
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  ctx.drawImage(SKY_C, 0, 0);
  drawStars(t);
  drawMoon();
  drawWisps(t);
  ctx.drawImage(VOLC_C, 0, 0);
  drawPlume(t);
  drawPlanes(t);
  ctx.drawImage(FAR.c, 0, 0);
  drawFarFx(t);
  drawFireworks(t);
  if (wx.marigold) for (const b of PETALS[0]) drawPetal(b, t);
  halo(HL.ba, BA.cx, 132, 0.95);                    // the floodlit air round the palace
  drawTL(t);
  drawLN(t);
  drawLane(t);                                      // before the palace: a trolleybus comes out from behind its plaza
  drawBA(t);
  drawMid(t);
  drawWeather(ctx, wx.shared, 0, t, wxc[0], reducedMotion);
  if (billboard) drawBillboard(t, pitch);
  drawWalk(t);
  ctx.drawImage(STREET_C, 0, 0);
  drawTraffic(t);
  drawWeather(ctx, wx.shared, 1, t, wxc[1], reducedMotion);
  if (wx.marigold) for (const b of PETALS[1]) drawPetal(b, t);
  drawNeighbour(t);
  drawForeground(t, wx.marigold);
  drawPapel(t);
  if (wx.marigold) for (const b of PETALS[2]) drawPetal(b, t);
  drawWeather(ctx, wx.shared, 2, t, wxc[2], reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'mexico-city',
  name: 'Mexico City',
  tagline: 'Día de Muertos at Bellas Artes',
  timeZone: 'America/Mexico_City',
  accent: '#8338ec',                                   // = ACCENT, written out: tools/seo/seo.mjs reads it from the source
  focusX: BA.cx,
  landmark: [BA.x0, BA.x1],                            // the Palacio de Bellas Artes, west flank to the east Pegasus
  landmarkTop: TL.tip,                                 // the Torre Latinoamericana's antenna, which wide crops keep
  billboard: BB,
  ambience: { traffic: 0.35, crowd: 0.32, wind: 0.12 },
  events: [
    { t: TROLLEYS[0].a + 1.5, type: 'horn', x: 300, gain: 0.18 },
    ...ORGAN_T.map(([a]) => ({ t: a + 0.4, type: 'chime', x: ORGAN.x, gain: 0.3 })),
    ...TOLLS.map(t => ({ t, type: 'bell', x: BELL.x, gain: 0.4 })),
    { t: AMBU_T, type: 'siren', dur: 11, pan: [-0.9, 0.5], gain: 0.35 },
    ...BURSTS.map(b => ({ t: +(b.t + 1.4).toFixed(1), type: 'firework', x: b.x, gain: 0.28 })),
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
